import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  ClipboardList, 
  Plus, 
  Search,
  ArrowUpDown,
  Grid,
  List,
  Phone,
  MapPin,
  X,
  Check,
  Send,
  Home,
  Building2,
  Landmark,
  MoreVertical,
  Edit,
  Link as LinkIcon,
  Sparkles,
  RefreshCcw,
  Bell,
  BellOff,
  Eye,
  Trash2,
  Copy,
  Share2,
  Loader,
  Network,
  Archive,
  Filter,
  Download
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { formatPrice, formatArea, generateWhatsAppLink, copyToClipboard, formatNumberWithCommas, parseFormattedNumber } from '../utils/helpers';
import { buildOfferShareText, buildOfferShareUrl } from '../utils/offerShareUtils';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useNetworkStatus } from '../context/NetworkStatusContext';
import { requestsService, matchingService, locationService, offersService } from '../services';
import PermissionGate from '../components/PermissionGate';
import LimitBadge from '../components/LimitBadge';
import SearchableSelect from '../components/SearchableSelect';
import MultiSelectSearchable from '../components/MultiSelectSearchable';
import DropdownMenu from '../components/DropdownMenu';
import GooglePagination from '../components/GooglePagination';
import OfferDetailsModal from '../components/OfferDetailsModal';
import './RequestsAndMatchingPage.css';

const normalizeSaudiPhone = (phone) => {
  // Accept local and international Saudi formats, then save one clean local format.
  let normalizedPhone = String(phone || '').replace(/[\s\-\(\)]/g, '');
  if (normalizedPhone.startsWith('00966')) {
    normalizedPhone = '0' + normalizedPhone.slice(5);
  } else if (normalizedPhone.startsWith('+966')) {
    normalizedPhone = '0' + normalizedPhone.slice(4);
  } else if (normalizedPhone.startsWith('966')) {
    normalizedPhone = '0' + normalizedPhone.slice(3);
  } else if (normalizedPhone.startsWith('5')) {
    normalizedPhone = '0' + normalizedPhone;
  }
  return normalizedPhone;
};

const RequestsAndMatchingPage = ({ creationMode = false }) => {
  const toast = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'ar';
  const { user } = useAuth();
  const { networkEnabled } = useNetworkStatus();
  const isSub = user?.accountType === 'sub';
  const subPerms = user?.permissions || {};
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from URL query param
    return 'requests'; // تبويب المطابقة داخل هذه الصفحة متوقف مؤقتاً.
  });

  useEffect(() => {
    // Normalize old direct matching links without affecting matching elsewhere in the system.
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'matching') navigate('/requests', { replace: true });
  }, [location.search, navigate]);
  
  // Requests State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterRequestType, setFilterRequestType] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [filterDistrict, setFilterDistrict] = useState('all');
  const [filterBudgetMin, setFilterBudgetMin] = useState('');
  const [filterBudgetMax, setFilterBudgetMax] = useState('');
  const [filterAreaMin, setFilterAreaMin] = useState('');
  const [filterAreaMax, setFilterAreaMax] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterApprovalStatus, setFilterApprovalStatus] = useState('all');
  const [showNotionFilters, setShowNotionFilters] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [showMobileSortSheet, setShowMobileSortSheet] = useState(false);
  const [showMobileFabMenu, setShowMobileFabMenu] = useState(false);
  const [mobileDensity, setMobileDensity] = useState('comfortable');
  // Internal creation now renders only on the protected /requests/new page.
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Matching State
  const [matchFilterType, setMatchFilterType] = useState('all');
  const [showMatchedOnly, setShowMatchedOnly] = useState(false);
  const [expandedRequestGroups, setExpandedRequestGroups] = useState({});
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [runningMatching, setRunningMatching] = useState(false);

  // API state
  const [requests, setRequests] = useState([]);
  const [matches, setMatches] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [matchesLoading, setMatchesLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, total: 1, count: 0 });
  const [matchesPagination, setMatchesPagination] = useState({ current: 1, total: 1, count: 0 });
  const [requestsCount, setRequestsCount] = useState(0);
  const [matchesCount, setMatchesCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [exportingRequests, setExportingRequests] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [autoSaveCountdown] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const autoSaveTimerRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const autoSaveIntervalRef = useRef(null);

  // Network visibility modal state
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [networkRequest, setNetworkRequest] = useState(null);

  // Location state
  const [cities, setCities] = useState([]);
  const [areasByCity, setAreasByCity] = useState({});
  const [districtsByArea, setDistrictsByArea] = useState({});
  const [allDistrictsByCity, setAllDistrictsByCity] = useState({});
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Fetch cities from API (public - managed by admin)
  const fetchCities = useCallback(async () => {
    try {
      setLoadingLocations(true);
      const response = await locationService.getAllCities(currentLang);
      if (response.success && response.data.cities) {
        const priorityCities = ['الجبيل', 'الدمام'];
        const citiesData = [
          ...priorityCities.map(name => response.data.cities.find(c => (c.arLabel || c.label) === name)).filter(Boolean),
          ...response.data.cities.filter(c => !priorityCities.includes(c.arLabel || c.label)),
        ];
        setCities(citiesData);
        // Build areas and districts maps
        const areasMap = {};
        const districtsMap = {};
        const allDistrictsMap = {};
        citiesData.forEach((city) => {
          areasMap[city.label] = (city.areas || []).map((a) => a.label);
          const cityAllDistricts = [];

          (city.areas || []).forEach((area) => {
            const areaDistricts = (area.districts || []).map((d) => d.label);
            districtsMap[area.label] = areaDistricts;
            cityAllDistricts.push(...areaDistricts);
          });

          (city.unassignedDistricts || []).forEach((d) => cityAllDistricts.push(d.label));
          allDistrictsMap[city.label] = cityAllDistricts;
        });

        setAreasByCity(areasMap);
        setDistrictsByArea(districtsMap);
        setAllDistrictsByCity(allDistrictsMap);
      }
    } catch (err) {
      console.error('Error fetching cities:', err);
    } finally {
      setLoadingLocations(false);
    }
  }, [currentLang]);

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  const getAreasForCity = (cityLabel) => {
    return areasByCity[cityLabel] || [];
  };

  const getDistrictsForArea = (areaLabel) => {
    return districtsByArea[areaLabel] || [];
  };

  const getAllDistrictsForCity = useCallback((cityLabel) => {
    return allDistrictsByCity[cityLabel] || [];
  }, [allDistrictsByCity]);

  const currentRequestsPage = pagination.current;
  const currentMatchesPage = matchesPagination.current;

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: currentRequestsPage,
        limit: 20
      };

      if (searchQuery) params.search = searchQuery;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterType !== 'all') params.propertyType = filterType;
      if (filterRequestType !== 'all') params.requestType = filterRequestType;
      if (filterCity !== 'all') params.city = filterCity;
      if (filterDistrict !== 'all') params.district = filterDistrict;
      if (filterBudgetMin) params.budgetMin = Number(filterBudgetMin);
      if (filterBudgetMax) params.budgetMax = Number(filterBudgetMax);
      if (filterAreaMin) params.areaMin = Number(filterAreaMin);
      if (filterAreaMax) params.areaMax = Number(filterAreaMax);
      if (filterDateFrom) params.dateFrom = filterDateFrom;
      if (filterDateTo) params.dateTo = filterDateTo;
      if (filterApprovalStatus !== 'all') params.approvalStatus = filterApprovalStatus;

      if (sortBy === 'newest' || sortBy === 'oldest') {
        params.sortBy = 'createdAt';
        params.sortOrder = sortBy === 'newest' ? 'desc' : 'asc';
      } else if (sortBy === 'budget_high' || sortBy === 'budget_low') {
        params.sortBy = 'budget';
        params.sortOrder = sortBy === 'budget_high' ? 'desc' : 'asc';
      } else if (sortBy === 'area_high' || sortBy === 'area_low') {
        params.sortBy = 'area';
        params.sortOrder = sortBy === 'area_high' ? 'desc' : 'asc';
      } else if (sortBy === 'name_asc' || sortBy === 'name_desc') {
        params.sortBy = 'clientName';
        params.sortOrder = sortBy === 'name_asc' ? 'asc' : 'desc';
      }

      const response = await requestsService.getMyRequests(params);
      if (response.success) {
        const transformedRequests = (response.data.requests || []).map((request) => ({
          ...request,
          budget: request.budget ?? request.budgetMax ?? '',
          area: request.area ?? request.areaMin ?? request.area?.min ?? '',
          areaMax: request.areaMax ?? request.area?.max ?? '',
          clientPhone: request.clientPhone || '',
          clientSeriousness: request.clientSeriousness || '',
          lastContact: request.lastContact || '',
          addedAt: request.addedAt || request.createdAt || '',
          approvalStatus: request.approvalStatus || 'approved',
          source: request.source || 'user'
        }));

        setRequests(transformedRequests);
        const pg = response.data.pagination || { current: 1, total: 1, count: transformedRequests.length };
        setPagination({
          current: pg.current || 1,
          total: pg.total || 1,
          count: pg.count || transformedRequests.length
        });
        setRequestsCount(pg.count || transformedRequests.length);
      } else {
        setRequests([]);
      }
    } catch (err) {
      setError(err.message || t('errors.loadFailed'));
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [
    currentRequestsPage,
    searchQuery,
    filterStatus,
    filterType,
    filterRequestType,
    filterCity,
    filterDistrict,
    filterBudgetMin,
    filterBudgetMax,
    filterAreaMin,
    filterAreaMax,
    filterDateFrom,
    filterDateTo,
    filterApprovalStatus,
    sortBy,
    t
  ]);

  const fetchMatches = useCallback(async () => {
    try {
      setMatchesLoading(true);
      const params = {
        page: currentMatchesPage,
        limit: 20,
        sortBy: 'score',
        sortOrder: 'desc'
      };

      if (matchFilterType !== 'all') {
        params.propertyType = matchFilterType;
      }

      const response = await matchingService.getMatches(params);
      if (response.success) {
        const dataMatches = response.data.matches || response.data || [];
        setMatches(dataMatches);
        const pg = response.data.pagination || { current: 1, total: 1, count: dataMatches.length };
        setMatchesPagination({
          current: pg.current || 1,
          total: pg.total || 1,
          count: pg.count || dataMatches.length
        });
        setMatchesCount(pg.count || dataMatches.length);
      } else {
        setMatches([]);
      }
    } catch (err) {
      setMatches([]);
      console.error('Failed to load matches:', err);
    } finally {
      setMatchesLoading(false);
    }
  }, [currentMatchesPage, matchFilterType]);

  const fetchCounts = useCallback(async () => {
    try {
      const [requestsRes, matchesRes] = await Promise.all([
        requestsService.getMyRequests({ page: 1, limit: 1 }),
        matchingService.getMatches({ page: 1, limit: 1 })
      ]);

      if (requestsRes.success) {
        setRequestsCount(requestsRes.data.pagination?.count || 0);
      }

      if (matchesRes.success) {
        setMatchesCount(matchesRes.data.pagination?.count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch counts:', err);
    }
  }, []);

  const handleRunMatching = useCallback(async () => {
    if (runningMatching) return;

    try {
      setRunningMatching(true);
      const response = await matchingService.runMatchingForAll();
      if (response.success) {
        toast.success(t('matching.matchingCompleted', { count: response.data?.totalMatches || 0 }));
        fetchMatches();
        fetchCounts();
      } else {
        toast.error(response.message || t('errors.matchingFailed'));
      }
    } catch (err) {
      console.error('Error running matching:', err);
      toast.error(err.message || t('errors.matchingFailed'));
    } finally {
      setRunningMatching(false);
    }
  }, [runningMatching, fetchMatches, fetchCounts, t, toast]);

  useEffect(() => {
    if (activeTab === 'requests') {
      fetchRequests();
    } else {
      fetchMatches();
    }
  }, [activeTab, fetchRequests, fetchMatches]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [
    filterStatus,
    filterType,
    filterRequestType,
    filterCity,
    filterDistrict,
    filterBudgetMin,
    filterBudgetMax,
    filterAreaMin,
    filterAreaMax,
    sortBy
  ]);

  useEffect(() => {
    setMatchesPagination((prev) => ({ ...prev, current: 1 }));
  }, [matchFilterType]);

  const filteredRequests = requests;

  // Get unique values from current requests for filter dropdowns
  const getUniqueCitiesFromRequests = () => [...new Set(requests.filter(r => r.city).map(r => r.city))];
  const getUniqueDistrictsFromRequests = () => {
    const filtered = filterCity !== 'all' ? requests.filter(r => r.city === filterCity) : requests;
    const allDistricts = [];
    filtered.forEach(r => {
      if (r.districts) allDistricts.push(...r.districts);
      else if (r.district) allDistricts.push(r.district);
    });
    return [...new Set(allDistricts)];
  };

  // AI state for requests
  const [aiRequestText, setAiRequestText] = useState('');
  const [analyzingRequest, setAnalyzingRequest] = useState(false);
  const [, setAddRequestMethod] = useState('ai');
  const [showMoreRequestFields, setShowMoreRequestFields] = useState(false);
  const [showMoreEditRequestFields, setShowMoreEditRequestFields] = useState(false);
  // 0 = choice screen, 'ai' = AI input, 1 = العميل, 2 = الطلب, 3 = التفاصيل
  const [currentRequestStep, setCurrentRequestStep] = useState(0);

  // FIX 3: all pre-selected defaults cleared so user must actively choose
  const [newRequest, setNewRequest] = useState({
    clientName: '',
    clientPhone: '',
    propertyType: '',
    requestType: '',
    city: '',
    region: '',
    districts: [],
    budget: '',
    area: '',
    paymentMethod: '',
    paymentPlan: '',
    age: '',
    floor: '',
    clientSeriousness: '',
    lastContact: '',
    requirements: '',
    notes: '',
    status: 'updated',
    shareInNetwork: true,
    landPurpose: '',
    streetWidth: '',
    direction: '',
    noBudgetPreference: false,
    noAreaPreference: false,
    noAgePreference: false
  });

  const hasUnsavedNewRequest = useMemo(() => {
    // Ignore untouched defaults; only user-entered creation data requires confirmation.
    return Boolean(
      aiRequestText.trim() ||
      newRequest.clientName.trim() ||
      newRequest.clientPhone.trim() ||
      newRequest.propertyType ||
      newRequest.requestType ||
      newRequest.city ||
      newRequest.region ||
      newRequest.districts.length > 0 ||
      String(newRequest.budget).trim() ||
      String(newRequest.area).trim() ||
      String(newRequest.age).trim() ||
      newRequest.requirements.trim() ||
      newRequest.notes.trim()
    );
  }, [aiRequestText, newRequest]);

  const requestCreationReturnPath = location.state?.from === '/dashboard' ? '/dashboard' : '/requests';

  // Close modal confirmation handlers
  const handleCloseAddModal = useCallback(() => {
    if (hasUnsavedNewRequest && !window.confirm('هل تريد إغلاق النموذج؟ سيتم فقد البيانات غير المحفوظة.')) return;

    setCurrentRequestStep(0);
    setShowMoreRequestFields(false);
    if (creationMode) navigate(requestCreationReturnPath);
  }, [creationMode, hasUnsavedNewRequest, navigate, requestCreationReturnPath]);

  const handleAnalyzeRequest = useCallback(async () => {
    if (!aiRequestText.trim()) {
      toast.warning(t('matching.writeDescriptionFirst'));
      return false;
    }
    if (!newRequest.city) {
      toast.warning('⚠️ يرجى اختيار المدينة أولاً قبل استخدام الذكاء الاصطناعي');
      return false;
    }
    setAnalyzingRequest(true);
    try {
      const response = await requestsService.analyzeWithAI(aiRequestText, newRequest.city);
      if (response.success && response.data.extractedData) {
        const data = response.data.extractedData;
        let finalCity = newRequest.city || data.city || '';
        let finalDistrict = data.district || '';

        if (finalCity && !cities.some(c => c.label === finalCity)) {
          const foundCity = cities.find(c =>
            c.label.includes(finalCity) || finalCity.includes(c.label)
          );
          if (foundCity) finalCity = foundCity.label;
        }

        const availableDistricts = getAllDistrictsForCity(finalCity);
        if (finalDistrict && availableDistricts.length > 0 && !availableDistricts.includes(finalDistrict)) {
          const foundDistrict = availableDistricts.find(d =>
            d.includes(finalDistrict) || finalDistrict.includes(d)
          );
          finalDistrict = foundDistrict || '';
        }

        let validDistricts = [];
        if (data.districts && data.districts.length > 0 && availableDistricts.length > 0) {
          validDistricts = data.districts.filter(d => {
            if (availableDistricts.includes(d)) return true;
            return !!availableDistricts.find(ad => ad.includes(d) || d.includes(ad));
          }).map(d => {
            if (availableDistricts.includes(d)) return d;
            return availableDistricts.find(ad => ad.includes(d) || d.includes(ad)) || d;
          });
        }

        let requirementsText = aiRequestText;
        if (data.clientName) requirementsText = requirementsText.replace(new RegExp(data.clientName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '').trim();
        if (data.clientPhone) requirementsText = requirementsText.replace(new RegExp(data.clientPhone.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '').trim();
        requirementsText = requirementsText.replace(/[^\S\n]+/g, ' ').replace(/\n{3,}/g, '\n\n').replace(/^\s*[,،-]\s*/, '').replace(/\s*[,،-]\s*$/, '').trim();

        const hasBudget = Number(data.budget) > 0;
        const hasArea = Number(data.area) > 0;
        const hasAge = Number(data.age) > 0;

        setNewRequest(prev => ({
          ...prev,
          clientName: data.clientName || prev.clientName,
          clientPhone: data.clientPhone || prev.clientPhone,
          propertyType: data.propertyType || prev.propertyType || 'any',
          requestType: data.requestType || prev.requestType,
          city: finalCity,
          districts: validDistricts.length > 0 ? validDistricts : (finalDistrict ? [finalDistrict] : prev.districts),
          budget: hasBudget ? String(data.budget) : '',
          area: hasArea ? String(data.area) : '',
          paymentMethod: data.paymentMethod || prev.paymentMethod,
          paymentPlan: data.paymentPlan || prev.paymentPlan,
          age: hasAge ? String(data.age) : '',
          floor: data.floor || prev.floor,
          requirements: requirementsText,
          noBudgetPreference: !hasBudget,
          noAreaPreference: !hasArea,
          noAgePreference: !hasAge
        }));
        setAddRequestMethod('form');

        if (finalCity && (data.districts?.length > 0 || finalDistrict)) {
          toast.success('✅ تم التحليل بنجاح! تم تعبئة جميع البيانات');
        } else if (finalCity) {
          toast.success('✅ تم التحليل! يرجى اختيار الحي يدوياً');
        } else {
          toast.success('✅ تم التحليل! يرجى اختيار المدينة والحي يدوياً');
        }
        return true;
      } else {
        toast.error(t('requests.aiAnalysisFailed'));
        return false;
      }
    } catch (err) {
      toast.error(t('requests.errorOccurred') + ': ' + err.message);
      return false;
    } finally {
      setAnalyzingRequest(false);
    }
  }, [aiRequestText, newRequest.city, cities, getAllDistrictsForCity, t, toast]);

  const handleCloseEditModal = useCallback(() => {
    if (window.confirm('هل تريد إغلاق النموذج؟ سيتم فقد التعديلات غير المحفوظة.')) {
      setShowEditModal(false);
      setEditingRequest(null);
    }
  }, []);

  // Districts are now loaded from API via fetchCities

  // Select all districts handler
  // Removed - not currently used

  // Removed - not currently used

  const [selectedMessage, setSelectedMessage] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [editRequestStep, setEditRequestStep] = useState(1); // خطوات تعديل الطلب: 1-2
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [showMoreMenu, setShowMoreMenu] = useState(null);
  const [showViewOfferModal, setShowViewOfferModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [ignoredMatches] = useState([]);
  const [showStatusMenu, setShowStatusMenu] = useState(null);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [showViewRequestModal, setShowViewRequestModal] = useState(false);
  const [showViewRequestMatches, setShowViewRequestMatches] = useState(false);
  const [viewRequestMatches, setViewRequestMatches] = useState([]);
  const [viewRequestMatchesLoading, setViewRequestMatchesLoading] = useState(false);
  const [viewRequestMatchesError, setViewRequestMatchesError] = useState('');
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateRequests, setDuplicateRequests] = useState([]);
  const [pendingRequestData, setPendingRequestData] = useState(null);
  const [showMatchingPopup, setShowMatchingPopup] = useState(false);
  const [matchingPopupResults, setMatchingPopupResults] = useState([]);
  const [matchingPopupLoading, setMatchingPopupLoading] = useState(false);
  const [updatingRequestId, setUpdatingRequestId] = useState(null);
  // Track the one-time post-save request flow and its short retry timer.
  const handledCreatedRequestRef = useRef(null);
  const newRequestMatchFlowRef = useRef(null);
  const requestsPageMountedRef = useRef(false);

  useEffect(() => {
    const anyModalOpen = showEditModal || showDeleteModal
      || showViewRequestModal || showViewOfferModal || showContactModal
      || showNetworkModal || showMatchingPopup;
    document.body.style.overflow = anyModalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showEditModal, showDeleteModal, showViewRequestModal,
      showViewOfferModal, showContactModal, showNetworkModal, showMatchingPopup]);

  const formatGregorianDate = useCallback((value, options = {}) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('ar-EG', {
      calendar: 'gregory',
      ...options
    });
  }, []);

  useEffect(() => {
    const hasOpenForm = (creationMode && hasUnsavedNewRequest) || showEditModal;
    if (!hasOpenForm) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [creationMode, hasUnsavedNewRequest, showEditModal]);

  const getMatchRequestId = useCallback((match) => String(match?.request?._id || match?.request?.id || match?.requestId || 'unknown-request'), []);
  const getMatchOfferId = useCallback((match) => String(match?.offer?._id || match?.offer?.id || match?.offerId || 'unknown-offer'), []);

  // Use matches data from API - backend already filters by propertyType
  const filteredMatches = matches.filter(match => {
    const scoreMatch = !showMatchedOnly || match.score >= 80;
    const notIgnored = !ignoredMatches.includes(match._id);
    return scoreMatch && notIgnored;
  });

  const dedupedMatches = useMemo(() => {
    const uniquePairs = new Map();

    filteredMatches.forEach((match) => {
      const reqId = getMatchRequestId(match);
      const offerId = getMatchOfferId(match);
      const key = `${reqId}::${offerId}`;
      const existing = uniquePairs.get(key);

      if (!existing || (Number(match.score) || 0) > (Number(existing.score) || 0)) {
        uniquePairs.set(key, match);
      }
    });

    return Array.from(uniquePairs.values());
  }, [filteredMatches, getMatchOfferId, getMatchRequestId]);

  const groupedMatches = useMemo(() => {
    const grouped = {};

    dedupedMatches.forEach((match) => {
      const reqId = getMatchRequestId(match);
      if (!grouped[reqId]) {
        grouped[reqId] = { reqId, request: match.request || match.requestData || {}, matches: [] };
      }
      grouped[reqId].matches.push(match);

      if (!grouped[reqId].request || !grouped[reqId].request._id) {
        grouped[reqId].request = match.request || grouped[reqId].request;
      }
    });

    const groups = Object.values(grouped);
    groups.forEach((group) => {
      group.matches.sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0));
    });

    groups.sort((a, b) => {
      const aTime = new Date(a.request?.createdAt || 0).getTime() || 0;
      const bTime = new Date(b.request?.createdAt || 0).getTime() || 0;
      return bTime - aTime;
    });

    return groups;
  }, [dedupedMatches, getMatchRequestId]);

  useEffect(() => {
    if (groupedMatches.length === 0) return;

    setExpandedRequestGroups((prev) => {
      let changed = false;
      const next = { ...prev };

      groupedMatches.forEach((group) => {
        if (next[group.reqId] === undefined) {
          next[group.reqId] = true;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [groupedMatches]);

  // Handlers
  const handleAddRequest = useCallback(async (confirmDuplicate = false) => {
    if (!newRequest.clientName || !newRequest.clientPhone || !newRequest.city || !newRequest.propertyType) {
      toast.warning(t('requests.errors.missingRequiredFields'));
      return;
    }
    if (!newRequest.budget && !newRequest.noBudgetPreference) {
      toast.warning(t('requests.errors.missingBudget'));
      return;
    }
    if (!newRequest.noBudgetPreference && Number(newRequest.budget) <= 0) {
      toast.warning('الميزانية يجب أن تكون أكبر من 0');
      return;
    }
    if (!newRequest.noAreaPreference && newRequest.area !== '' && Number(newRequest.area) <= 0) {
      toast.warning('المساحة يجب أن تكون أكبر من 0');
      return;
    }
    if (!newRequest.noAgePreference && newRequest.age !== '' && Number(newRequest.age) <= 0) {
      toast.warning('العمر يجب أن يكون أكبر من 0');
      return;
    }
    
    // Normalize phone number, including +966 55 559 0799.
    const normalizedPhone = normalizeSaudiPhone(newRequest.clientPhone);
    
    setSaving(true);
    try {
      const requestData = {
        ...newRequest,
        clientPhone: normalizedPhone,
        budget: newRequest.noBudgetPreference ? 0 : Number(newRequest.budget),
        age: newRequest.noAgePreference ? 0 : (newRequest.age ? Number(newRequest.age) : ''),
        area: newRequest.noAreaPreference ? '' : newRequest.area,
        district: newRequest.districts[0] || '',
        confirmDuplicate
      };
      
      const response = await requestsService.create(requestData);
      if (response.success) {
        // Check if this is a duplicate warning
        if (response.data?.isDuplicate) {
          setDuplicateRequests(response.data.existingRequests);
          setPendingRequestData(requestData);
          setShowDuplicateWarning(true);
          setSaving(false);
          return;
        }
        toast.success(t('requests.requestCreated'));
        setShowDuplicateWarning(false);
        setDuplicateRequests([]);
        setPendingRequestData(null);
        
        // The standalone page hands the new id to /requests for opening and match checks.
        const returnedRequestId = response.data?.request?._id || response.data?.request?.id || response.data?._id || response.data?.id;
        const createdRequestId = typeof returnedRequestId === 'string' && returnedRequestId.trim() ? returnedRequestId : null;
        // /requests consumes this state once, opens the request, and checks delayed matches.
        navigate('/requests', {
          replace: true,
          state: createdRequestId ? { openRequestId: createdRequestId, checkNewRequestMatches: true } : null
        });
        
        // FIX 3: reset matches the cleared initial state
        setNewRequest({
          clientName: '',
          clientPhone: '',
          propertyType: '',
          requestType: '',
          city: '',
          region: '',
          districts: [],
          budget: '',
          area: '',
          paymentMethod: '',
          paymentPlan: '',
          age: '',
          floor: '',
          clientSeriousness: '',
          lastContact: '',
          requirements: '',
          notes: '',
          shareInNetwork: true,
          landPurpose: '',
          streetWidth: '',
          direction: '',
          noBudgetPreference: false,
          noAreaPreference: false,
          noAgePreference: false
        });
      } else {
        toast.error(response.message || t('errors.saveFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [newRequest, navigate, t, toast]);

  const handleExportRequests = useCallback(async (format) => {
    try {
      setExportingRequests(true);
      const blob = await requestsService.exportMyRequests(format);
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const fileName = `requests_export_${new Date().toISOString().slice(0, 10)}.${ext}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`تم تصدير الطلبات بصيغة ${ext.toUpperCase()}`);
    } catch (error) {
      toast.error(error.message || 'فشل تصدير الطلبات');
    } finally {
      setExportingRequests(false);
    }
  }, [toast]);

  const handleConfirmDuplicate = useCallback(async () => {
    if (!pendingRequestData) return;
    setShowDuplicateWarning(false);
    setSaving(true);
    try {
      const response = await requestsService.create({ ...pendingRequestData, confirmDuplicate: true });
      if (response.success && !response.data?.isDuplicate) {
        toast.success(t('requests.requestCreated'));
        setDuplicateRequests([]);
        setPendingRequestData(null);
        
        // Duplicate confirmation follows the same standalone post-save handoff.
        const returnedRequestId = response.data?.request?._id || response.data?.request?.id || response.data?._id || response.data?.id;
        const createdRequestId = typeof returnedRequestId === 'string' && returnedRequestId.trim() ? returnedRequestId : null;
        navigate('/requests', {
          replace: true,
          state: createdRequestId ? { openRequestId: createdRequestId, checkNewRequestMatches: true } : null
        });
        
        // FIX 3: reset matches the cleared initial state
        setNewRequest({
          clientName: '',
          clientPhone: '',
          propertyType: '',
          requestType: '',
          city: '',
          region: '',
          districts: [],
          budget: '',
          area: '',
          paymentMethod: '',
          paymentPlan: '',
          age: '',
          floor: '',
          clientSeriousness: '',
          lastContact: '',
          requirements: '',
          notes: '',
          shareInNetwork: true,
          landPurpose: '',
          streetWidth: '',
          direction: '',
          noBudgetPreference: false,
          noAreaPreference: false,
          noAgePreference: false
        });
      } else {
        toast.error(response.message || t('errors.saveFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [pendingRequestData, navigate, t, toast]);

  const handleContact = (request) => {
    setSelectedRequest(request);
    setSelectedMessage(t('requests.defaultMessage', { name: request.clientName }));
    setShowContactModal(true);
  };

  const handleSendWhatsApp = async () => {
    if (!selectedRequest) return;
    const link = generateWhatsAppLink(selectedRequest.clientPhone, selectedMessage);
    window.open(link, '_blank');
    
    // Update status to updated via API
    try {
      await requestsService.changeStatus(selectedRequest._id || selectedRequest.id, 'updated');
      fetchRequests();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
    setShowContactModal(false);
  };

  const handleCopyFormLink = async () => {
    const userId = user?._id || user?.id;
    const formLink = userId 
      ? `${window.location.origin}/request-form?ref=${userId}`
      : `${window.location.origin}/request-form`;
    await copyToClipboard(formLink);
    setCopiedLink(true);
    toast.success('تم نسخ الرابط بنجاح');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const getPublicOfferLink = useCallback((offer) => {
    const offerId = offer?._id || offer?.id;
    return buildOfferShareUrl({
      username: offer?.username || offer?.marketer?.username || offer?.user?.username,
      catalogSlug: offer?.catalogSlug || offer?.catalog?.slug,
      offerId
    });
  }, []);

  const openOfferOwnerWhatsApp = useCallback((offer, introText = 'مرحباً، أنا مهتم بالعرض:') => {
    if (!offer?.ownerPhone) return;

    let phone = String(offer.ownerPhone).replace(/[\s-]/g, '');
    if (phone.startsWith('0')) phone = `966${phone.slice(1)}`;
    else if (!phone.startsWith('966') && !phone.startsWith('+966')) phone = `966${phone}`;
    phone = phone.replace('+', '');

    const offerInfo = `${offer.title || 'عرض عقاري'}\n${offer.city || ''}${offer.district ? ` - ${offer.district}` : ''}\nالسعر: ${formatPrice(offer.price)}\n${offer.area ? `المساحة: ${formatArea(offer.area)}` : ''}`;
    const offerLink = getPublicOfferLink(offer);
    const message = buildOfferShareText({ text: `${introText}\n${offerInfo}`, url: offerLink });

    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  }, [getPublicOfferLink]);

  const handleContactClient = (match) => {
    const offerLink = getPublicOfferLink(match.offer);
    const text = `السلام عليكم ${match.request.clientName}\n\nيوجد عرض مطابق لطلبك\n\n${match.offer.title || 'عرض عقاري'}\n\n${match.offer.city || ''}${match.offer.district ? ' - ' + match.offer.district : ''}\n\nالسعر: ${formatPrice(match.offer.price)} ر.س\n\nالمساحة: ${match.offer.area ? formatArea(match.offer.area) : '-'}`;
    const message = buildOfferShareText({ text, url: offerLink });
    window.open(generateWhatsAppLink(match.request.clientPhone, message), '_blank');
  };

  // Send all matching offers for a request in one WhatsApp message to the client
  const handleBulkWhatsApp = (group) => {
    const { request, matches } = group;
    const clientName = request.clientName || 'العميل';
    const clientPhone = request.clientPhone;
    if (!clientPhone || matches.length === 0) return;

    const arabicNumbers = ['الأول', 'الثاني', 'الثالث', 'الرابع', 'الخامس', 'السادس', 'السابع', 'الثامن', 'التاسع', 'العاشر'];

    const offerLines = matches.map((match, index) => {
      const label = arabicNumbers[index] || `${index + 1}`;
      const offerLink = getPublicOfferLink(match.offer);
      return buildOfferShareText({ text: `العرض ${label}\n${match.offer.title || 'عرض عقاري'}`, url: offerLink });
    }).join('\n\n');

    const message = `السلام عليكم ${clientName}\nيوجد ${matches.length} من العروض المطابقة لطلبك\n\n${offerLines}`;
    window.open(generateWhatsAppLink(clientPhone, message), '_blank');
  };

  // Edit Request
  const handleOpenEdit = useCallback((request) => {
    const areaVal = request.area || request.areaMin;
    const ageVal = request.age;
    const budgetVal = request.budget;
    setEditingRequest({
      ...request,
      noBudgetPreference: !budgetVal || Number(budgetVal) === 0,
      noAreaPreference: !areaVal || Number(areaVal) === 0,
      noAgePreference: !ageVal || Number(ageVal) === 0,
    });
    setEditRequestStep(1);
    setShowMoreEditRequestFields(false);
    setShowEditModal(true);
    setShowMoreMenu(null);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingRequest) return;

    if (!editingRequest.noBudgetPreference && Number(editingRequest.budget) <= 0) {
      toast.warning('الميزانية يجب أن تكون أكبر من 0');
      return;
    }

    const requestAreaValue = editingRequest.area || editingRequest.areaMin;
    if (!editingRequest.noAreaPreference && requestAreaValue !== '' && requestAreaValue !== undefined && requestAreaValue !== null && Number(requestAreaValue) <= 0) {
      toast.warning('المساحة يجب أن تكون أكبر من 0');
      return;
    }

    if (!editingRequest.noAgePreference && editingRequest.age !== '' && editingRequest.age !== undefined && editingRequest.age !== null && Number(editingRequest.age) <= 0) {
      toast.warning('العمر يجب أن يكون أكبر من 0');
      return;
    }

    setSaving(true);
    try {
      // Normalize edited client phone before sending it to the backend.
      const requestToSend = {
        ...editingRequest,
        clientPhone: normalizeSaudiPhone(editingRequest.clientPhone)
      };
      const response = await requestsService.update(editingRequest._id || editingRequest.id, requestToSend);
      if (response.success) {
        toast.success(t('requests.requestUpdated'));
        fetchRequests();
        setShowEditModal(false);
        setEditingRequest(null);
      } else {
        toast.error(response.message || t('errors.saveFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  }, [editingRequest, fetchRequests, t, toast]);

  // Delete Request
  const handleOpenDelete = useCallback((request) => {
    setRequestToDelete(request);
    setShowDeleteModal(true);
    setShowMoreMenu(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!requestToDelete) return;
    setSaving(true);
    try {
      const response = await requestsService.archive(requestToDelete._id || requestToDelete.id);
      if (response.success) {
        toast.success('تم أرشفة الطلب بنجاح');
        fetchRequests();
        setShowDeleteModal(false);
        setRequestToDelete(null);
      } else {
        toast.error(response.message || t('errors.deleteFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.deleteFailed'));
    } finally {
      setSaving(false);
    }
  }, [requestToDelete, fetchRequests, t, toast]);

  // Update Status
  const handleQuickUpdateRequest = useCallback(async (request) => {
    const requestId = request._id || request.id;
    if (!requestId) {
      toast.error(t('errors.updateFailed'));
      return;
    }
    setUpdatingRequestId(requestId);
    try {
      const statusResponse = await requestsService.changeStatus(requestId, 'updated');
      const alreadyUpdated = request.status === 'updated';
      if (!statusResponse.success && !alreadyUpdated) {
        toast.error(statusResponse.message || t('errors.updateFailed'));
        return;
      }

      // Re-run matching for this request so user sees fresh matches after update.
      try {
        await matchingService.runMatching(requestId);
      } catch (matchingErr) {
        console.error('Failed to run matching after request update:', matchingErr);
      }

      toast.success(alreadyUpdated ? 'تم تحديث الطلب وإعادة فحص المطابقة' : t('requests.statusUpdated'));
      fetchRequests();
    } catch (err) {
      toast.error(err.message || t('errors.updateFailed'));
    } finally {
      setUpdatingRequestId((prev) => (prev === requestId ? null : prev));
    }
  }, [fetchRequests, t, toast]);

  const handleUpdateStatus = useCallback(async (requestId, newStatus) => {
    try {
      const response = await requestsService.changeStatus(requestId, newStatus);
      if (response.success) {
        toast.success(t('requests.statusUpdated'));
        fetchRequests();
      } else {
        toast.error(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.updateFailed'));
    }
    setShowMoreMenu(null);
  }, [fetchRequests, t, toast]);

  const handleReviewPublicRequest = useCallback(async (request, action) => {
    const requestId = request?._id || request?.id;
    if (!requestId) return;
    setSaving(true);
    try {
      // قبول الطلب يجعله نشطاً، والرفض يبقيه خارج المطابقة بدون حذفه.
      const response = await requestsService.reviewPublicMarketerRequest(requestId, action);
      if (response.success) {
        toast.success(action === 'approve' ? 'تم قبول الطلب وتحويله لطلب نشط' : 'تم رفض الطلب');
        fetchRequests();
      } else {
        toast.error(response.message || t('errors.updateFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('errors.updateFailed'));
    } finally {
      setSaving(false);
      setShowMoreMenu(null);
    }
  }, [fetchRequests, t, toast]);

  const handlePublicRequestWhatsApp = useCallback((request) => {
    const message = `السلام عليكم ${request.clientName || ''}، وصلنا طلبك عبر موقعي العقاري وسنتابع معك التفاصيل.`;
    window.open(generateWhatsAppLink(request.clientPhone, message), '_blank');
  }, []);

  // Property Types for lookup
  const propertyTypesMap = useMemo(() => ({
    villa: 'فيلا',
    apartment: 'شقة',
    land: 'أرض',
    building: 'عمارة',
    duplex: 'دوبلكس',
    farm: 'مزرعة',
    rest_house: 'استراحة',
    floor: 'دور',
    shop: 'محل',
    warehouse: 'مستودع',
    studio: 'استديو'
  }), []);

  // Share Request
  const handleShareRequest = useCallback(async (request) => {
    const shareText = `${t('requests.realEstateRequest')}:\n${t('requests.type')}: ${propertyTypesMap[request.propertyType] || request.propertyType}\n${t('offers.city')}: ${request.city}${request.districts?.length > 0 ? ` - ${request.districts.join('، ')}` : ''}\n${t('requests.budget')}: ${formatPrice(request.budget)}${request.area ? `\nالمساحة: ${formatArea(request.area)}` : ''}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'طلب عقاري', text: shareText });
      } catch (err) {
        // User canceled the share - do nothing
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    } else {
      copyToClipboard(shareText);
      toast.success(t('common.copied'));
    }
    setShowMoreMenu(null);
  }, [propertyTypesMap, t, toast]);

  // Open network visibility modal
  const handleOpenNetworkModal = useCallback((request) => {
    setNetworkRequest(request);
    setShowNetworkModal(true);
    setShowMoreMenu(null);
  }, []);

  // Save network visibility settings (toggle share in network) (toggle share in network)
  const handleSaveNetworkSettings = useCallback(async () => {
    if (!networkRequest) return;
    
    try {
      const response = await requestsService.toggleNetwork(networkRequest._id || networkRequest.id);
      
      if (response.success) {
        // Update local state
        setRequests(prev => prev.map(r => 
          (r._id || r.id) === (networkRequest._id || networkRequest.id) 
            ? { ...r, shareInNetwork: response.data.shareInNetwork }
            : r
        ));
        const message = response.data.shareInNetwork 
          ? t('requests.addedToNetwork') 
          : t('requests.removedFromNetwork');
        toast.success(message);
        setShowNetworkModal(false);
        setNetworkRequest(null);
      } else {
        toast.error(response.message || t('requests.networkSaveFailed'));
      }
    } catch (err) {
      toast.error(err.message || t('requests.networkSaveFailed'));
    }
  }, [networkRequest, toast, t]);

  // View Offer Details
  const handleViewOffer = useCallback(async (match) => {
    setSelectedMatch(match);
    setShowViewOfferModal(true);

    const offerId = match?.offer?._id || match?.offer?.id || match?.offerId;
    if (!offerId) return;

    try {
      const response = await offersService.getById(offerId);
      if (response.success) {
        const fullOffer = response.data?.offer || response.data;
        if (!fullOffer) return;

        setSelectedMatch((prev) => {
          if (!prev) return prev;
          const prevOfferId = String(prev?.offer?._id || prev?.offer?.id || prev?.offerId || '');
          if (prevOfferId !== String(offerId)) return prev;

          return {
            ...prev,
            offer: {
              ...(prev.offer || {}),
              ...fullOffer
            }
          };
        });
      }
    } catch (_) {
      // Keep modal with the currently available match payload if full details request fails.
    }
  }, []);

  const resetViewRequestMatchesState = useCallback(() => {
    setShowViewRequestMatches(false);
    setViewRequestMatches([]);
    setViewRequestMatchesLoading(false);
    setViewRequestMatchesError('');
  }, []);

  // Cancel delayed retries when the request closes, changes, or the page unmounts.
  const cancelNewRequestMatchFlow = useCallback(() => {
    const flow = newRequestMatchFlowRef.current;
    if (!flow) return;
    flow.cancelled = true;
    if (flow.timerId) clearTimeout(flow.timerId);
    if (flow.resolveTimer) flow.resolveTimer();
    newRequestMatchFlowRef.current = null;
  }, []);

  useEffect(() => {
    requestsPageMountedRef.current = true;
    // Ignore React StrictMode's immediate development replay, but clean real unmounts.
    return () => {
      requestsPageMountedRef.current = false;
      setTimeout(() => {
        if (!requestsPageMountedRef.current) cancelNewRequestMatchFlow();
      }, 0);
    };
  }, [cancelNewRequestMatchFlow]);

  const openRequestDetails = useCallback((request, options = {}) => {
    if (!options.preserveNewRequestMatchFlow) cancelNewRequestMatchFlow();
    setViewingRequest(request);
    setShowViewRequestModal(true);
  }, [cancelNewRequestMatchFlow]);

  const handleToggleViewRequestMatches = useCallback(async () => {
    const requestId = viewingRequest?._id || viewingRequest?.id;
    if (!requestId) return;

    const willOpen = !showViewRequestMatches;
    setShowViewRequestMatches(willOpen);

    if (!willOpen || viewRequestMatches.length > 0) {
      return;
    }

    try {
      setViewRequestMatchesLoading(true);
      setViewRequestMatchesError('');

      const response = await matchingService.getMatchesForRequest(requestId, {
        page: 1,
        limit: 30,
        sortBy: 'score',
        sortOrder: 'desc'
      });

      if (response.success) {
        const rawMatches = response.data?.matches || [];
        const uniqueByOffer = new Map();

        rawMatches.forEach((match) => {
          const offerId = String(match?.offer?._id || match?.offer?.id || match?.offerId || match?._id || '');
          const existing = uniqueByOffer.get(offerId);
          if (!existing || (Number(match.score) || 0) > (Number(existing.score) || 0)) {
            uniqueByOffer.set(offerId, match);
          }
        });

        setViewRequestMatches(Array.from(uniqueByOffer.values()));
      } else {
        setViewRequestMatches([]);
        setViewRequestMatchesError(response.message || 'تعذر تحميل العروض المطابقة');
      }
    } catch (err) {
      setViewRequestMatches([]);
      setViewRequestMatchesError(err?.message || 'تعذر تحميل العروض المطابقة');
    } finally {
      setViewRequestMatchesLoading(false);
    }
  }, [showViewRequestMatches, viewRequestMatches.length, viewingRequest]);

  useEffect(() => {
    const requestId = location.state?.openRequestId;
    if (creationMode || !requestId || loading || handledCreatedRequestRef.current === requestId) return;

    // A newer post-save request replaces and cancels any older delayed check.
    cancelNewRequestMatchFlow();
    handledCreatedRequestRef.current = requestId;
    const shouldCheckMatches = location.state?.checkNewRequestMatches === true;
    const flow = { requestId, cancelled: false, timerId: null, resolveTimer: null };
    newRequestMatchFlowRef.current = flow;

    // Consume the one-time navigation instruction before any asynchronous work.
    navigate('/requests', { replace: true, state: null });

    const waitBeforeRetry = () => new Promise((resolve) => {
      flow.resolveTimer = resolve;
      flow.timerId = setTimeout(() => {
        flow.timerId = null;
        flow.resolveTimer = null;
        resolve();
      }, 800);
    });

    const openCreatedRequest = async () => {
      try {
        let createdRequest = requests.find((request) => String(request._id || request.id) === String(requestId));
        if (!createdRequest) {
          const response = await requestsService.getById(requestId);
          createdRequest = response.success ? (response.data?.request || response.data) : null;
        }
        if (flow.cancelled || !createdRequest) return;

        openRequestDetails(createdRequest, { preserveNewRequestMatchFlow: true });
        if (!shouldCheckMatches) return;

        setMatchingPopupLoading(true);
        let receivedValidResponse = false;

        // One immediate request plus two retries, with no continuous polling.
        for (let attempt = 0; attempt < 3 && !flow.cancelled; attempt += 1) {
          if (attempt > 0) await waitBeforeRetry();
          if (flow.cancelled) return;

          try {
            const matchesResponse = await matchingService.getMatchesForRequest(requestId, {
              page: 1, limit: 30, sortBy: 'score', sortOrder: 'desc'
            });
            if (flow.cancelled) return;
            const requestMatches = matchesResponse?.data?.matches;
            if (!Array.isArray(requestMatches)) continue;

            receivedValidResponse = true;
            if (requestMatches.length > 0) {
              setViewRequestMatches(requestMatches);
              setMatchingPopupResults(requestMatches);
              setMatchingPopupLoading(false);
              setShowMatchingPopup(true);
              return;
            }
          } catch (matchError) {
            // Matching failures never undo the successful request save or detail opening.
            console.error('Error loading new request matches:', matchError);
          }
        }

        if (!flow.cancelled && receivedValidResponse) {
          toast.info('تم حفظ الطلب ولم توجد مطابقات حاليًا.');
        }
      } catch (_) {
        // The request was saved even if its follow-up detail fetch is unavailable.
      } finally {
        if (!flow.cancelled) setMatchingPopupLoading(false);
        if (newRequestMatchFlowRef.current === flow) newRequestMatchFlowRef.current = null;
      }
    };

    openCreatedRequest();
  }, [cancelNewRequestMatchFlow, creationMode, loading, location.state, navigate, openRequestDetails, requests, toast]);

  useEffect(() => {
    resetViewRequestMatchesState();
  }, [viewingRequest, resetViewRequestMatchesState]);

  // Helper Functions
  const getStatusClass = (status) => {
    const classes = {
      not_updated: 'status-not-updated',
      updated: 'status-updated'
    };
    return classes[status] || '';
  };

  const getStatusLabel = (status) => {
    const labels = {
      not_updated: t('requests.notUpdated'),
      updated: t('requests.updated')
    };
    return labels[status] || status;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'score-excellent';
    if (score >= 75) return 'score-good';
    if (score >= 50) return 'score-fair';
    return 'score-low';
  };

  // Reuse the same matched-offer rows in request details and the post-save popup.
  const renderRequestMatchesList = ({ requestMatches, loading: requestMatchesLoading, error: requestMatchesError, onViewOffer }) => (
    <div className="mrc-matches-list">
      {requestMatchesLoading && <div className="mrc-matches-empty">جاري تحميل العروض المطابقة...</div>}
      {!requestMatchesLoading && requestMatchesError && <div className="mrc-matches-empty">{requestMatchesError}</div>}
      {!requestMatchesLoading && !requestMatchesError && requestMatches.length === 0 && (
        <div className="mrc-matches-empty">لا يوجد عروض مطابقة حالياً</div>
      )}
      {!requestMatchesLoading && !requestMatchesError && requestMatches.map((match) => {
        const offer = match.offer || {};
        const offerId = offer._id || offer.id || match._id;
        return (
          <div key={`view-request-match-${offerId}`} className="mrc-match-item">
            <span className={`mrc-match-score ${getScoreColor(match.score)}`}>{match.score || 0}%</span>
            <div className="mrc-match-main">
              <div className="mrc-match-title">{offer.title || 'عرض عقاري'}</div>
              <div className="mrc-match-meta">{offer.city || '-'}{offer.district ? ` - ${offer.district}` : ''} • {formatPrice(offer.price || 0)}</div>
            </div>
            <div className="mrc-match-actions">
              <button type="button" className="mrc-match-view-btn" onClick={() => onViewOffer(match)}>
                عرض
              </button>
              <button
                type="button"
                className="mrc-match-view-btn"
                onClick={() => {
                  const link = getPublicOfferLink(offer);
                  if (link) navigator.clipboard.writeText(link).then(() => toast.success('تم نسخ الرابط'));
                }}
                title="نسخ رابط العرض"
              >
                <Copy size={14} />
              </button>
              {offer.ownerPhone && (
                <button
                  type="button"
                  className="mrc-match-whatsapp-btn"
                  onClick={() => openOfferOwnerWhatsApp(offer, 'مرحباً، أنا مهتم بالعرض:')}
                  title="تواصل واتساب"
                >
                  <FaWhatsapp size={14} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const getMissingRequestFields = useCallback((request) => {
    const missing = [];

    if (!request.clientName) missing.push('اسم العميل');
    if (!request.clientPhone) missing.push('رقم الجوال');
    if (!request.requestType) missing.push('نوع الطلب');
    if (!request.propertyType) missing.push('نوع العقار');
    if (!request.city) missing.push('المدينة');
    if (!(request.districts?.length > 0 || request.district)) missing.push('الحي');
    if (!(Number(request.budget) > 0 || request.noBudgetPreference)) missing.push('الميزانية');
    if (!(Number(request.area || request.areaMin) > 0 || request.noAreaPreference)) missing.push('المساحة');

    return missing;
  }, []);

  // Simplified property types list
  const propertyTypes = [
    { value: 'any', label: 'لا يهم' },
    { value: 'villa', label: t('offers.villa') },
    { value: 'apartment', label: t('offers.apartment') },
    { value: 'land', label: t('offers.land') },
    { value: 'building', label: t('offers.building') },
    { value: 'duplex', label: t('offers.duplex') },
    { value: 'farm', label: t('offers.farm') },
    { value: 'rest_house', label: t('offers.rest_house') },
    { value: 'floor', label: t('offers.floor') },
    { value: 'shop', label: t('offers.shop') },
    { value: 'warehouse', label: t('offers.warehouse') },
    { value: 'studio', label: t('offers.studio') }
  ];

  const messageTemplates = [
    { label: 'رسالة العثور على عروض', value: t('requests.defaultMessage', { name: selectedRequest?.clientName || '' }) },
    { label: 'رسالة المتابعة', value: t('requests.followUpMessage', { name: selectedRequest?.clientName || '' }) }
  ];

  const hasActiveFilters =
    filterRequestType !== 'all' ||
    filterType !== 'all' ||
    filterStatus !== 'all' ||
    filterCity !== 'all' ||
    filterDistrict !== 'all' ||
    filterBudgetMin ||
    filterBudgetMax ||
    filterAreaMin ||
    filterAreaMax ||
    filterApprovalStatus !== 'all' ||
    filterDateFrom ||
    filterDateTo;

  const activeFiltersCount = [
    filterRequestType !== 'all',
    filterType !== 'all',
    filterStatus !== 'all',
    filterCity !== 'all',
    filterDistrict !== 'all',
    filterBudgetMin,
    filterBudgetMax,
    filterAreaMin,
    filterAreaMax,
    filterApprovalStatus !== 'all',
    filterDateFrom,
    filterDateFrom,
    filterDateTo,
    filterDateTo
  ].filter(Boolean).length;

  const clearAllRequestFilters = () => {
    setFilterRequestType('all');
    setFilterType('all');
    setFilterStatus('all');
    setFilterCity('all');
    setFilterDistrict('all');
    setFilterBudgetMin('');
    setFilterBudgetMax('');
    setFilterAreaMin('');
    setFilterAreaMax('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  // Every internal add action uses the same standalone request form.
  const openNewRequestPage = () => navigate('/requests/new', { state: { from: '/requests' } });

  return (
    <PermissionGate feature="PAGE_REQUESTS" type="page">
    <div className={`requests-matching-page${creationMode ? ' new-request-page' : ''}`}>
      {!creationMode && (
      <>
      {/* Page Header */}
      <div className="page-header">
        <div className="header-title">
          <ClipboardList size={28} />
          <h1>{t('requests.title')}</h1>
          {activeTab === 'requests' && <LimitBadge limitCode="LIMIT_REQUESTS" currentCount={requests.length} />}
        </div>
        <div className="header-actions desktop-header-actions">
          {activeTab === 'requests' && (
            <>
              <button className="btn btn-outline" onClick={handleCopyFormLink}>
                <LinkIcon size={18} />
                {copiedLink ? t('common.copied') : t('requests.copyFormLink')}
              </button>
              {!(isSub && subPerms.addRequests === false) && (
              <PermissionGate
                feature="LIMIT_REQUESTS"
                type="limit"
                currentCount={requests.length}
                hideWhenDenied={false}
                fallback={
                  <button className="btn btn-disabled" disabled>
                    <Plus size={20} />
                    {t('requests.addRequest')}
                  </button>
                }
              >
                <button className="btn btn-primary" data-tour="add-request-btn" onClick={openNewRequestPage}>
                  <Plus size={20} />
                  {t('requests.addRequest')}
                </button>
              </PermissionGate>
              )}
            </>
          )}
          {activeTab === 'matching' && (
            <>
              <button 
                className="btn btn-primary"
                onClick={handleRunMatching}
                disabled={runningMatching}
              >
                {runningMatching ? <Loader size={18} className="spinner" /> : <RefreshCcw size={18} />}
                {t('matching.runMatching')}
              </button>
              <button 
                className={`btn ${notificationsEnabled ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => setNotificationsEnabled(!notificationsEnabled)}
              >
                {notificationsEnabled ? <Bell size={18} /> : <BellOff size={18} />}
                {notificationsEnabled ? t('matching.notificationsOn') : t('matching.notificationsOff')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="page-tabs">
        <button 
          className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          <ClipboardList size={20} />
          <span>{t('requests.title')}</span>
          <span className="tab-count">{requestsCount}</span>
        </button>
        <button
          className="tab-btn tab-btn-disabled"
          data-tour="matching-tab"
          disabled
          aria-disabled="true"
        >
          <Sparkles size={20} />
          <span>{t('matching.title')}</span>
          <span className="coming-soon-badge">قريبًا</span>
          <span className="tab-count">{matchesCount}</span>
        </button>
      </div>

      {/* Requests Tab Content */}
      {activeTab === 'requests' && (
        <>
          {/* Filters */}
          <div className="filters-section">
            <div className="search-box">
              <Search size={20} />
              <input 
                type="text"
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="filters notion-desktop-filters" style={{ alignItems: 'center' }}>
              <button 
                className={`notion-filter-btn ${showNotionFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                onClick={() => {
                  setShowNotionFilters(!showNotionFilters);
                  setShowMobileSortSheet(false);
                }}
              >
                <Filter size={18} />
                {activeFiltersCount > 0 ? <span className="filter-count-badge">{activeFiltersCount}</span> : null}
              </button>
              {hasActiveFilters && (
                <button className="clear-all-inline" onClick={clearAllRequestFilters}>✕</button>
              )}

              <div className="filter-group">
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="newest">الأحدث أولاً</option>
                  <option value="oldest">الأقدم أولاً</option>
                  <option value="budget_high">الميزانية (الأعلى أولاً)</option>
                  <option value="budget_low">الميزانية (الأقل أولاً)</option>
                  <option value="area_high">المساحة (الأعلى أولاً)</option>
                  <option value="area_low">المساحة (الأقل أولاً)</option>
                  <option value="name_asc">الاسم: أ-ي</option>
                  <option value="name_desc">الاسم: ي-أ</option>
                </select>
              </div>
            </div>

            {/* أزرار التصدير — ديسكتوب */}
            <div className="notion-desktop-filters" style={{ gap: '6px' }}>
              <button className="btn btn-outline btn-sm" onClick={() => handleExportRequests('xlsx')} disabled={exportingRequests} title="تصدير Excel">
                <Download size={16} />
                <span>Excel</span>
              </button>
              <button className="btn btn-outline btn-sm" onClick={() => handleExportRequests('pdf')} disabled={exportingRequests} title="تصدير PDF">
                <Download size={16} />
                <span>PDF</span>
              </button>
            </div>

            <div className="mobile-actions-row" aria-label="أدوات الطلبات">
              <button
                className={`mobile-icon-btn ${showNotionFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                onClick={() => {
                  setShowNotionFilters(!showNotionFilters);
                  setShowMobileSortSheet(false);
                }}
                title="الفلاتر"
              >
                <Filter size={17} />
                {activeFiltersCount > 0 ? <span className="filter-count-badge">{activeFiltersCount}</span> : null}
              </button>

              <button
                className={`mobile-icon-btn ${showMobileSortSheet ? 'active' : ''}`}
                onClick={() => {
                  setShowMobileSortSheet(!showMobileSortSheet);
                  setShowNotionFilters(false);
                }}
                title="الفرز"
              >
                <ArrowUpDown size={17} />
              </button>

              <button
                className="mobile-icon-btn"
                onClick={handleCopyFormLink}
                title={copiedLink ? t('common.copied') : t('requests.copyFormLink')}
              >
                <LinkIcon size={16} />
              </button>

              {/* زر تصدير — جوال */}
              <button
                className="mobile-icon-btn"
                onClick={() => handleExportRequests('xlsx')}
                disabled={exportingRequests}
                title="تصدير Excel"
              >
                <Download size={16} />
              </button>

              <div className="mobile-view-toggle" role="group" aria-label="كثافة العرض">
                <button
                  className={mobileDensity === 'compact' ? 'active' : ''}
                  onClick={() => setMobileDensity('compact')}
                  title="عرض مضغوط"
                >
                  <Grid size={16} />
                </button>
                <button
                  className={mobileDensity === 'comfortable' ? 'active' : ''}
                  onClick={() => setMobileDensity('comfortable')}
                  title="عرض مريح"
                >
                  <List size={16} />
                </button>
              </div>
            </div>

            <div className="mobile-export-row" aria-label="إضافة طلب">
              {!(isSub && subPerms.addRequests === false) && (
                <PermissionGate
                  feature="LIMIT_REQUESTS"
                  type="limit"
                  currentCount={requests.length}
                  hideWhenDenied={false}
                  fallback={
                    <button className="btn btn-disabled" disabled style={{ width: '100%' }}>
                      <Plus size={18} />
                      {t('requests.addRequest')}
                    </button>
                  }
                >
                  <button className="btn btn-primary" data-tour="add-request-btn" style={{ width: '100%' }} onClick={openNewRequestPage}>
                    <Plus size={18} />
                    {t('requests.addRequest')}
                  </button>
                </PermissionGate>
              )}
            </div>
          </div>

          {/* Notion-style Filters Panel */}
          {showNotionFilters && (
            <div className="notion-sheet-backdrop" onClick={() => setShowNotionFilters(false)}>
              <div className="notion-filters-panel" onClick={(e) => e.stopPropagation()}>
                <div className="notion-filters-header">
                  <span>الفلاتر</span>
                  {hasActiveFilters && (
                    <button className="notion-clear-btn" onClick={clearAllRequestFilters}>مسح الكل</button>
                  )}
                </div>
                <div className="notion-filters-body">
                <div className="notion-filter-row">
                  <label>نوع الطلب</label>
                  <select value={filterRequestType} onChange={(e) => setFilterRequestType(e.target.value)}>
                    <option value="all">{t('common.all')}</option>
                    <option value="buy">{t('requests.buy')}</option>
                    <option value="rent">{t('requests.rent')}</option>
                  </select>
                </div>
                <div className="notion-filter-row">
                  <label>{t('offers.propertyType')}</label>
                  <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                    <option value="all">{t('common.all')}</option>
                    {propertyTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="notion-filter-row">
                  <label>{t('offers.city')}</label>
                  <select value={filterCity} onChange={(e) => { setFilterCity(e.target.value); setFilterDistrict('all'); }}>
                    <option value="all">{t('common.all')}</option>
                    {getUniqueCitiesFromRequests().map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <div className="notion-filter-row">
                  <label>الحي</label>
                  <select value={filterDistrict} onChange={(e) => setFilterDistrict(e.target.value)}>
                    <option value="all">{t('common.all')}</option>
                    {getUniqueDistrictsFromRequests().map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div className="notion-filter-row">
                  <label>الميزانية</label>
                  <div className="notion-range-inputs">
                    <input type="text" placeholder="من" value={formatNumberWithCommas(filterBudgetMin)} onChange={(e) => setFilterBudgetMin(parseFormattedNumber(e.target.value))} />
                    <span>-</span>
                    <input type="text" placeholder="إلى" value={formatNumberWithCommas(filterBudgetMax)} onChange={(e) => setFilterBudgetMax(parseFormattedNumber(e.target.value))} />
                  </div>
                </div>
                <div className="notion-filter-row">
                  <label>المساحة (م²)</label>
                  <div className="notion-range-inputs">
                    <input type="text" placeholder="من" value={formatNumberWithCommas(filterAreaMin)} onChange={(e) => setFilterAreaMin(parseFormattedNumber(e.target.value))} />
                    <span>-</span>
                    <input type="text" placeholder="إلى" value={formatNumberWithCommas(filterAreaMax)} onChange={(e) => setFilterAreaMax(parseFormattedNumber(e.target.value))} />
                  </div>
                </div>
                <div className="notion-filter-row">
                  <label>تاريخ الإضافة</label>
                  <div className="notion-date-range-inputs">
                    <label className="notion-date-field-label">
                      <span>من</span>
                      <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
                    </label>
                    <label className="notion-date-field-label">
                      <span>إلى</span>
                      <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
                    </label>
                  </div>
                </div>
                <div className="notion-filter-row">
                  <label>الحالة</label>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="all">{t('common.all')}</option>
                    <option value="not_updated">{t('requests.notUpdated')}</option>
                    <option value="updated">{t('requests.updated')}</option>
                  </select>
                </div>
                <div className="notion-filter-row">
                  <label>طلبات موقعي</label>
                  <select value={filterApprovalStatus} onChange={(e) => setFilterApprovalStatus(e.target.value)}>
                    <option value="all">كل الطلبات غير المرفوضة</option>
                    <option value="pending">بانتظار الموافقة</option>
                    <option value="approved">مقبولة</option>
                    <option value="rejected">مرفوضة</option>
                  </select>
                </div>
                </div>
              </div>
            </div>
          )}

          {showMobileSortSheet && (
            <div className="notion-sheet-backdrop" onClick={() => setShowMobileSortSheet(false)}>
              <div className="notion-sort-panel" onClick={(e) => e.stopPropagation()}>
                <div className="notion-filters-header">
                  <span>الفرز</span>
                </div>
                <div className="notion-sort-options">
                  {[
                    { value: 'newest', label: 'الأحدث أولاً' },
                    { value: 'oldest', label: 'الأقدم أولاً' },
                    { value: 'budget_high', label: 'الميزانية (الأعلى أولاً)' },
                    { value: 'budget_low', label: 'الميزانية (الأقل أولاً)' },
                    { value: 'area_high', label: 'المساحة (الأعلى أولاً)' },
                    { value: 'area_low', label: 'المساحة (الأقل أولاً)' },
                    { value: 'name_asc', label: 'الاسم: أ-ي' },
                    { value: 'name_desc', label: 'الاسم: ي-أ' }
                  ].map((option) => (
                    <button
                      key={option.value}
                      className={`sort-option-btn ${sortBy === option.value ? 'active' : ''}`}
                      onClick={() => {
                        setSortBy(option.value);
                        setShowMobileSortSheet(false);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className={`mobile-fab-stack ${showMobileFabMenu ? 'open' : ''}`}>
            <div className="mobile-fab-menu" role="menu" aria-label="إجراءات سريعة">
              <button className="mobile-fab-action" onClick={() => handleExportRequests('xlsx')} disabled={exportingRequests}>
                <Download size={16} />
                <span>تصدير Excel</span>
              </button>
              <button className="mobile-fab-action" onClick={() => handleExportRequests('pdf')} disabled={exportingRequests}>
                <Download size={16} />
                <span>تصدير PDF</span>
              </button>
              <button className="mobile-fab-action" onClick={handleCopyFormLink}>
                <LinkIcon size={16} />
                <span>{copiedLink ? t('common.copied') : t('requests.copyFormLink')}</span>
              </button>
              <PermissionGate
                feature="LIMIT_REQUESTS"
                type="limit"
                currentCount={requests.length}
                hideWhenDenied={false}
                fallback={
                  <button className="mobile-fab-action" disabled title={t('permissions.limitReached')}>
                    <Plus size={16} />
                    <span>{t('permissions.limitReached')}</span>
                  </button>
                }
              >
                <button
                  className="mobile-fab-action"
                  onClick={() => {
                    setShowMobileFabMenu(false);
                    openNewRequestPage();
                  }}
                >
                  <Plus size={16} />
                  <span>{t('requests.addRequest')}</span>
                </button>
              </PermissionGate>
            </div>

            <button
              className="mobile-fab-main"
              onClick={() => setShowMobileFabMenu(!showMobileFabMenu)}
              aria-label="إجراءات"
            >
              {showMobileFabMenu ? <X size={18} /> : <Plus size={20} />}
            </button>
          </div>

          {/* Requests List */}
          {filteredRequests.length === 0 ? (
            <div className="empty-state">
              <ClipboardList size={64} />
              <h3>{t('requests.noRequests')}</h3>
              <p>{t('requests.emptyHelp')}</p>
              <div className="empty-actions">
                <button className="btn btn-primary" onClick={openNewRequestPage}>
                  <Plus size={20} />
                  {t('requests.addRequest')}
                </button>
                <button className="btn btn-outline" onClick={handleCopyFormLink}>
                  <LinkIcon size={18} />
                  {t('requests.sendFormLink')}
                </button>
              </div>
            </div>
          ) : (
            <div className={`requests-list ${mobileDensity === 'compact' ? 'compact-mode' : ''}`}>
              {filteredRequests.map(request => {
                const missingRequestFields = getMissingRequestFields(request);
                const requestId = request._id || request.id;
                const matchesCountValue = Number(request.matchingOffersCount ?? request.matchesCount ?? 0);
                const requestTypeLabel = request.requestType === 'buy' ? t('requests.buy') : t('requests.rent');
                const requestAreaValue = Number(request.area || request.areaMin || 0);
                const requestAddedAt = request.addedAt || request.createdAt;
                const isPendingPublicRequest = request.source === 'public_marketer_site' && request.approvalStatus === 'pending';

                return (
                <div key={requestId} className={`modern-request-card ${isPendingPublicRequest ? 'pending-public-request' : ''}`} onClick={() => openRequestDetails(request)} style={{ cursor: 'pointer' }}>
                  <div className="mrc-minimal-layout">
                    <div className="mrc-minimal-main">
                      <h3 className="mrc-client-name">{request.clientName || 'بدون اسم'}</h3>
                      {isPendingPublicRequest && (
                        <span className="public-request-badge">وارد من موقعي العقاري — بانتظار الموافقة</span>
                      )}
                      <p className="mrc-card-summary">
                        {[
                          requestTypeLabel,
                          propertyTypes.find(pt => pt.value === request.propertyType)?.label,
                          request.city,
                          requestAreaValue > 0 ? `${formatArea(requestAreaValue)}` : null
                        ].filter(Boolean).join(' • ')}
                      </p>
                    </div>

                    <div className="mrc-actions" onClick={(e) => e.stopPropagation()}>
                      <span className={`mrc-match-icon-chip ${matchesCountValue > 0 ? 'has-matches' : 'no-matches'}`} title="عدد المطابقات">
                        <Sparkles size={14} />
                        <strong>{matchesCountValue}</strong>
                      </span>
                      {missingRequestFields.length > 0 && (
                        <button
                          type="button"
                          className="missing-fields-indicator"
                          onClick={() => toast.warning(`الحقول الناقصة: ${missingRequestFields.join('، ')}`)}
                        >
                          <span className="missing-icon">⚠</span>
                          <span className="missing-text">{missingRequestFields.length}</span>
                        </button>
                      )}
                      <div className="mrc-icon-row">
                      <DropdownMenu
                        isOpen={showMoreMenu === requestId}
                        onClose={() => { setShowMoreMenu(null); setShowStatusMenu(null); }}
                        trigger={
                          <button className="action-btn circle-btn-sm mrc-icon-btn" onClick={(e) => { e.stopPropagation(); setShowMoreMenu(showMoreMenu === requestId ? null : requestId); }}>
                            <MoreVertical size={18} />
                          </button>
                        }
                      >
                        <button onClick={() => { openRequestDetails(request); setShowMoreMenu(null); }}>
                          <Eye size={16} /> {t('common.view')}
                        </button>
                        <button onClick={() => { handleContact(request); setShowMoreMenu(null); }}>
                          <FaWhatsapp size={16} /> {t('requests.contactClient')}
                        </button>
                        {isPendingPublicRequest && (
                          <>
                            <button onClick={() => handleReviewPublicRequest(request, 'approve')}>
                              <Check size={16} /> قبول
                            </button>
                            <button onClick={() => handleReviewPublicRequest(request, 'reject')}>
                              <X size={16} /> رفض
                            </button>
                            <button onClick={() => { handlePublicRequestWhatsApp(request); setShowMoreMenu(null); }}>
                              <FaWhatsapp size={16} /> تواصل واتساب
                            </button>
                          </>
                        )}
                        <button onClick={() => { handleQuickUpdateRequest(request); setShowMoreMenu(null); }}>
                          {updatingRequestId === requestId ? <Loader size={16} className="spinner" /> : <RefreshCcw size={16} />} {t('requests.updateStatus')}
                        </button>
                        <button onClick={() => { handleOpenEdit(request); setShowMoreMenu(null); }}>
                          <Edit size={16} /> {t('common.edit')}
                        </button>
                        <div className="menu-item-with-submenu">
                          <button onClick={() => setShowStatusMenu(showStatusMenu === (request._id || request.id) ? null : (request._id || request.id))}>
                            <RefreshCcw size={16} /> {t('requests.updateStatus')}
                            <span className="arrow">◂</span>
                          </button>
                          {showStatusMenu === (request._id || request.id) && (
                            <div className="status-submenu">
                              <button 
                                className={request.status === 'not_updated' ? 'active' : ''}
                                onClick={() => { handleUpdateStatus(request._id || request.id, 'not_updated'); setShowStatusMenu(null); setShowMoreMenu(null); }}
                              >
                                <span className="status-dot not-updated"></span>
                                {t('requests.notUpdated')}
                              </button>
                              <button 
                                className={request.status === 'updated' ? 'active' : ''}
                                onClick={() => { handleUpdateStatus(request._id || request.id, 'updated'); setShowStatusMenu(null); setShowMoreMenu(null); }}
                              >
                                <span className="status-dot updated"></span>
                                {t('requests.updated')}
                              </button>
                            </div>
                          )}
                        </div>
                        {networkEnabled && (
                        <button onClick={() => { handleOpenNetworkModal(request); setShowMoreMenu(null); }}>
                          <Network size={16} /> {request.shareInNetwork ? 'طلب خاص' : t('offers.networkSettings')}
                        </button>
                        )}
                        <button onClick={() => { handleShareRequest(request); setShowMoreMenu(null); }}>
                          <Share2 size={16} /> {t('common.share')}
                        </button>
                        <button onClick={() => { copyToClipboard(request.clientPhone); setShowMoreMenu(null); toast.success(t('common.copied')); }}>
                          <Copy size={16} /> {t('common.copy')}
                        </button>
                        <button className="delete-option" onClick={() => { handleOpenDelete(request); setShowMoreMenu(null); }}>
                          <Trash2 size={16} /> أرشفة
                        </button>
                      </DropdownMenu>
                      </div>
                    </div>
                  </div>
                </div>
              );})}
            </div>
          )}

          {pagination.total > 1 && (
            <GooglePagination
              currentPage={pagination.current}
              totalPages={pagination.total}
              onPageChange={(page) => setPagination(prev => ({ ...prev, current: page }))}
              prevLabel={t('common.previous')}
              nextLabel={t('common.next')}
              infoPrefix={t('common.page')}
              infoMiddle={t('common.of')}
              isRTL={currentLang === 'ar'}
            />
          )}
        </>
      )}

      {/* Matching Tab Content */}
      {activeTab === 'matching' && (
        <>
          {/* Filters */}
          <div className="filters-section" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div className="filter-group">
              <select value={matchFilterType} onChange={(e) => setMatchFilterType(e.target.value)}>
                <option value="all">{t('matching.allTypes')}</option>
                {propertyTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <label className="toggle-filter">
              <input 
                type="checkbox" 
                checked={showMatchedOnly}
                onChange={(e) => setShowMatchedOnly(e.target.checked)}
              />
              <span>{t('matching.highMatchOnly')}</span>
            </label>

            <button 
              className="btn btn-primary" 
              onClick={handleRunMatching}
              disabled={runningMatching}
              style={{ marginRight: 'auto' }}
            >
              {runningMatching ? <Loader size={18} className="spinner" /> : <Sparkles size={18} />}
              {t('matching.runMatching')}
            </button>
          </div>

          {/* Matches grouped by request */}
          {dedupedMatches.length === 0 ? (
            <div className="empty-state">
              <Sparkles size={64} />
              <h3>{t('matching.noMatches')}</h3>
              <p>{t('matching.newMatchesNotification')}</p>
            </div>
          ) : (
            <div className="matches-by-request">
              {groupedMatches.map((group) => {
                const reqId = group.reqId;
                const requestTitle = `${group.request.requestType === 'buy' ? t('requests.buy') : t('requests.rent')} ${propertyTypes.find(pt => pt.value === group.request.propertyType)?.label || ''}`.trim();

                return (
                  <div key={reqId} className="request-matches-group" data-tour="matching-list">
                    <div
                      className={`request-matches-group-header ${expandedRequestGroups[reqId] ? 'expanded' : ''}`}
                    >
                      <div className="request-matches-group-title-wrap">
                        <h4 className="request-matches-group-title">{requestTitle || 'طلب عقاري'}</h4>
                      </div>
                      <span className="request-matches-group-count">
                        {group.matches.length} {group.matches.length === 1 ? 'عرض مطابق' : 'عروض مطابقة'}
                      </span>
                      <button
                        type="button"
                        className="btn btn-success request-match-offer-btn"
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', padding: '4px 10px' }}
                        title="إرسال جميع العروض عبر واتساب"
                        onClick={() => handleBulkWhatsApp(group)}
                      >
                        <FaWhatsapp size={14} />
                        إرسال الكل
                      </button>
                      <button
                        type="button"
                        className="request-matches-group-toggle"
                        onClick={() => setExpandedRequestGroups(prev => ({ ...prev, [reqId]: !prev[reqId] }))}
                      >
                        {expandedRequestGroups[reqId] ? 'إخفاء العروض' : 'عرض العروض'}
                      </button>
                    </div>
                    {/* Matching offers list - collapsible */}
                    {expandedRequestGroups[reqId] && <div className="request-matches-group-body">
                      {group.matches.map(match => (
                        <div key={match._id} className="request-match-offer-item" data-tour="matching-result">
                          <div className={`request-match-offer-score ${getScoreColor(match.score)}`}>
                            {match.score}%
                          </div>
                          <div className="request-match-offer-main">
                            <h5 className="request-match-offer-title">{match.offer.title || 'بدون عنوان'}</h5>
                            <p className="request-match-offer-meta">
                              {match.offer.city} - {match.offer.district} | {formatPrice(match.offer.price)} | {formatArea(match.offer.area)}
                              {match.offer.rooms ? ` | ${match.offer.rooms} غرف` : ''}
                            </p>
                          </div>
                          <div className="request-match-offer-actions">
                            <button
                              className="btn btn-success request-match-offer-btn"
                              onClick={() => handleContactClient(match)}
                              title={t('matching.contactClient')}
                            >
                              <FaWhatsapp size={14} />
                            </button>
                            <button
                              className="btn btn-outline request-match-offer-btn"
                              onClick={() => {
                                const link = getPublicOfferLink(match.offer);
                                if (link) {
                                  navigator.clipboard.writeText(link).then(() => toast.success('تم نسخ الرابط'));
                                }
                              }}
                              title="نسخ رابط العرض"
                            >
                              <Copy size={14} />
                            </button>
                            <button
                              className="btn btn-outline request-match-offer-btn"
                              onClick={() => handleViewOffer(match)}
                              title={t('matching.viewDetails')}
                            >
                              <Eye size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Matches Pagination */}
          {!matchesLoading && matches.length > 0 && matchesPagination.total > 1 && (
            <GooglePagination
              currentPage={matchesPagination.current}
              totalPages={matchesPagination.total}
              onPageChange={(page) => setMatchesPagination(prev => ({ ...prev, current: page }))}
              prevLabel={t('common.previous')}
              nextLabel={t('common.next')}
              infoPrefix={t('common.page')}
              infoMiddle={t('common.of')}
              isRTL={currentLang === 'ar'}
            />
            )}
        </>
      )}

      </>
      )}

      {/* Standalone internal request form */}
      {creationMode && (
        <div className="new-request-page-shell">
          <div className="modal-content large new-request-page-card" data-tour="request-form">
            <div className="modal-header">
              <h2>
                <Plus size={24} />
                {t('requests.addRequest')}
              </h2>
              <button className="btn btn-outline new-request-back-btn" onClick={handleCloseAddModal} aria-label="الرجوع">
                <X size={18} />
                رجوع
              </button>
            </div>
            
            <div className="modal-body">
              {/* Step 0: choice screen */}
              {currentRequestStep === 0 && (
                <div className="add-method-choice">
                  <button
                    className="choice-btn choice-btn-ai"
                    onClick={() => { setAddRequestMethod('ai'); setCurrentRequestStep('ai'); }}
                  >
                    <Sparkles size={22} />
                    إضافة بالذكاء الاصطناعي
                  </button>
                  <button
                    className="choice-btn choice-btn-form"
                    onClick={() => { setAddRequestMethod('form'); setCurrentRequestStep(1); }}
                  >
                    <Edit size={22} />
                    إضافة يدوي
                  </button>
                </div>
              )}

              {/* Step indicator for steps 1-2 — قابل للنقر للتنقل */}
              {typeof currentRequestStep === 'number' && currentRequestStep >= 1 && (
                <div className="step-indicator">
                  {[
                    { num: '١', label: 'العميل' },
                    { num: '٢', label: 'تفاصيل الطلب' },
                  ].map((step, index) => {
                    const stepNum = index + 1;
                    const canNavigate = stepNum <= currentRequestStep;
                    return (
                      <div
                        key={index}
                        className={`step-item ${currentRequestStep === stepNum ? 'active' : ''} ${currentRequestStep > stepNum ? 'done' : ''} ${canNavigate ? 'clickable' : ''}`}
                        onClick={() => canNavigate && setCurrentRequestStep(stepNum)}
                        style={canNavigate ? { cursor: 'pointer' } : {}}
                      >
                        <span className="step-dot">{step.num}</span>
                        <span className="step-label">{step.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Step 'ai': AI input screen */}
              {currentRequestStep === 'ai' && (
                <div className="ai-entry">
                  <div className="ai-header">
                    <Sparkles size={24} />
                    <h3>{t('matching.aiEntryTitle')}</h3>
                    <p>{t('matching.aiEntryDescription')}</p>
                  </div>

                  {/* City selection before AI analysis */}
                  <div className="form-group ai-city-select">
                    <label>{t('offers.selectCityForAI')}</label>
                    <SearchableSelect
                      options={cities.map(city => ({ value: city.label, label: city.label }))}
                      value={newRequest.city}
                      onChange={(value) => setNewRequest({ ...newRequest, city: value, districts: [] })}
                      placeholder={t('offers.selectCityOptional')}
                      searchPlaceholder={t('requests.searchCity')}
                      loading={loadingLocations}
                      disabled={loadingLocations}
                    />
                    <small className="hint">{t('offers.cityHintAI')}</small>
                  </div>

                  <textarea
                    className="form-control ai-textarea"
                    rows={6}
                    value={aiRequestText}
                    onChange={(e) => {
                      setAiRequestText(e.target.value);
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder={t('matching.aiPlaceholder')}
                  />
                </div>
              )}

              {/* Step 1: العميل */}
              {currentRequestStep === 1 && (
              <div className="form-section">
                <h3>{t('requests.clientData')}</h3>
                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>{t('requests.clientName')} <span className="required">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={newRequest.clientName}
                      onChange={(e) => setNewRequest({ ...newRequest, clientName: e.target.value })}
                      placeholder={t('requests.fullName')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('requests.clientPhone')} <span className="required">*</span></label>
                    <input
                      type="tel"
                      className="form-control"
                      value={newRequest.clientPhone}
                      onChange={(e) => setNewRequest({ ...newRequest, clientPhone: e.target.value })}
                      placeholder="+966 55 559 0799"
                      dir="ltr"
                    />
                  </div>
                </div>

                {/* زر اظهار المزيد */}
                <button
                  type="button"
                  className="show-more-fields-btn"
                  onClick={() => setShowMoreRequestFields(v => !v)}
                >
                  {showMoreRequestFields ? '▲ إخفاء' : '▼ اظهار المزيد'}
                </button>

                {showMoreRequestFields && (
                <>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('requests.clientSeriousness')}</label>
                    <select
                      className="form-control"
                      value={newRequest.clientSeriousness}
                      onChange={(e) => setNewRequest({ ...newRequest, clientSeriousness: e.target.value })}
                    >
                      <option value="">{t('requests.clientSeriousness')}</option>
                      <option value="very_interested">{t('requests.veryInterested')}</option>
                      <option value="interested">{t('requests.interested')}</option>
                      <option value="potential">{t('requests.potential')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>{t('requests.lastContact')}</label>
                    <input
                      type="date"
                      className="form-control"
                      value={newRequest.lastContact}
                      onChange={(e) => setNewRequest({ ...newRequest, lastContact: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>الحالة</label>
                    <select
                      className="form-control"
                      value={newRequest.status || 'updated'}
                      onChange={(e) => setNewRequest({ ...newRequest, status: e.target.value })}
                    >
                      <option value="updated">{t('requests.updated')}</option>
                      <option value="not_updated">{t('requests.notUpdated')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>ملاحظات خاصة</label>
                    <textarea
                      className="form-control expandable-textarea"
                      rows={3}
                      value={newRequest.notes || ''}
                      onChange={(e) => setNewRequest({ ...newRequest, notes: e.target.value })}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                      ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                      placeholder="ملاحظات خاصة بك عن هذا الطلب..."
                    />
                  </div>
                </div>
                </>
                )}
              </div>
              )}

              {/* Step 2: تفاصيل الطلب */}
              {currentRequestStep === 2 && (
              <div className="form-section">
                <h3>تفاصيل الطلب</h3>
                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>{t('requests.requestType')} <span className="required">*</span></label>
                    {/* FIX 3: added empty placeholder option */}
                    <select
                      className="form-control"
                      value={newRequest.requestType}
                      onChange={(e) => setNewRequest({ ...newRequest, requestType: e.target.value })}
                    >
                      <option value="">{t('requests.requestType')}</option>
                      <option value="buy">{t('requests.buy')}</option>
                      <option value="rent">{t('requests.rent')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('offers.propertyType')} <span className="required">*</span></label>
                    <select
                      className="form-control"
                      value={newRequest.propertyType}
                      onChange={(e) => setNewRequest({ ...newRequest, propertyType: e.target.value })}
                    >
                      <option value="">{t('requests.selectPropertyType')}</option>
                      {propertyTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row three-cols">
                  <div className="form-group">
                    <label>{t('offers.city')} <span className="required">*</span></label>
                    <SearchableSelect
                      options={cities.map(city => ({ value: city.label, label: city.label }))}
                      value={newRequest.city}
                      onChange={(value) => setNewRequest({ ...newRequest, city: value, region: '', districts: [] })}
                      placeholder={t('common.selectCity')}
                      searchPlaceholder={t('requests.searchCity')}
                      loading={loadingLocations}
                      disabled={loadingLocations}
                    />
                  </div>
                  <div className="form-group">
                    <label>المنطقة</label>
                    <SearchableSelect
                      options={newRequest.city ? getAreasForCity(newRequest.city).map(a => ({ value: a, label: a })) : []}
                      value={newRequest.region}
                      onChange={(value) => setNewRequest({ ...newRequest, region: value, districts: [] })}
                      placeholder="اختر المنطقة"
                      searchPlaceholder="ابحث عن المنطقة..."
                      disabled={!newRequest.city || loadingLocations}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('requests.districts')} <span className="required">*</span></label>
                    <MultiSelectSearchable
                      options={(newRequest.region ? getDistrictsForArea(newRequest.region) : getAllDistrictsForCity(newRequest.city)).map(d => ({ value: d, label: d }))}
                      value={newRequest.districts}
                      onChange={(selected) => setNewRequest({ ...newRequest, districts: selected })}
                      placeholder={t('requests.selectDistrictsRequired')}
                      searchPlaceholder={t('requests.searchDistrict')}
                      selectAllLabel={t('requests.selectAllDistricts')}
                      disabled={!newRequest.city || loadingLocations}
                    />
                  </div>
                </div>

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>
                      {t('requests.budget')} (ر.س) {!newRequest.noBudgetPreference && <span className="required">*</span>}
                      <label className="checkbox-label" style={{ display: 'inline-flex', marginRight: '12px', fontSize: '12px', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={newRequest.noBudgetPreference}
                          onChange={(e) => setNewRequest({ ...newRequest, noBudgetPreference: e.target.checked, budget: e.target.checked ? '' : newRequest.budget })}
                        />
                        <span>لا يهم</span>
                      </label>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={formatNumberWithCommas(newRequest.budget)}
                      onChange={(e) => setNewRequest({ ...newRequest, budget: parseFormattedNumber(e.target.value) })}
                      placeholder={newRequest.noBudgetPreference ? 'لا يهم' : t('requests.budget')}
                      disabled={newRequest.noBudgetPreference}
                    />
                  </div>

                  {/* Conditional fields based on request type */}
                  {newRequest.requestType === 'buy' ? (
                    <div className="form-group">
                      <label>{t('requests.paymentMethod')}</label>
                      <select
                        className="form-control"
                        value={newRequest.paymentMethod}
                        onChange={(e) => setNewRequest({ ...newRequest, paymentMethod: e.target.value })}
                      >
                        <option value="">اختر</option>
                        <option value="cash">{t('requests.paymentCash')}</option>
                        <option value="bank">{t('requests.paymentBank')}</option>
                      </select>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>{t('requests.paymentPlan')}</label>
                      <select
                        className="form-control"
                        value={newRequest.paymentPlan}
                        onChange={(e) => setNewRequest({ ...newRequest, paymentPlan: e.target.value })}
                      >
                        <option value="">اختر</option>
                        <option value="single">{t('requests.paymentSingle')}</option>
                        <option value="installments">{t('requests.paymentInstallments')}</option>
                        <option value="monthly">{t('requests.paymentMonthly')}</option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Land-specific fields */}
                {newRequest.propertyType === 'land' && (
                  <div className="form-row two-cols">
                    <div className="form-group">
                      <label>{t('offers.landPurpose')}</label>
                      <select
                        className="form-control"
                        value={newRequest.landPurpose}
                        onChange={(e) => setNewRequest({ ...newRequest, landPurpose: e.target.value })}
                      >
                        <option value="">{t('common.select') || 'اختر'}</option>
                        <option value="residential">{t('offers.purposeResidential')}</option>
                        <option value="agricultural">{t('offers.purposeAgricultural')}</option>
                        <option value="industrial">{t('offers.purposeIndustrial')}</option>
                        <option value="commercial">{t('offers.purposeCommercial')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('offers.street')} (م)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={newRequest.streetWidth}
                        onChange={(e) => setNewRequest({ ...newRequest, streetWidth: e.target.value })}
                        placeholder={t('offers.street')}
                      />
                    </div>
                  </div>
                )}

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>
                      {t('offers.area')} (م²)
                      <label className="checkbox-label" style={{ display: 'inline-flex', marginRight: '12px', fontSize: '12px', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={newRequest.noAreaPreference}
                          onChange={(e) => setNewRequest({ ...newRequest, noAreaPreference: e.target.checked, area: e.target.checked ? '' : newRequest.area })}
                        />
                        <span>لا يهم</span>
                      </label>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={newRequest.area}
                      onChange={(e) => setNewRequest({ ...newRequest, area: e.target.value })}
                      placeholder={newRequest.noAreaPreference ? 'لا يهم' : t('offers.area')}
                      disabled={newRequest.noAreaPreference}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      {t('requests.age')} (سنة)
                      <label className="checkbox-label" style={{ display: 'inline-flex', marginRight: '12px', fontSize: '12px', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={newRequest.noAgePreference}
                          onChange={(e) => setNewRequest({ ...newRequest, noAgePreference: e.target.checked, age: e.target.checked ? '' : newRequest.age })}
                        />
                        <span>لا يهم</span>
                      </label>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={newRequest.age}
                      onChange={(e) => setNewRequest({ ...newRequest, age: e.target.value })}
                      placeholder={newRequest.noAgePreference ? 'لا يهم' : t('requests.age')}
                      disabled={newRequest.noAgePreference}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('requests.requirements')}</label>
                    <textarea
                      className="form-control expandable-textarea"
                      rows={4}
                      value={newRequest.requirements}
                      onChange={(e) => setNewRequest({ ...newRequest, requirements: e.target.value })}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                      ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                      placeholder={t('requests.requirements')}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <small className="checkbox-hint">يتم تفعيل المطابقة تلقائياً لكل طلب محدث، وإيقافها عند تحويل الحالة إلى غير محدث.</small>
                  </div>
                </div>

                {/* خيار "مشاركة في السوق" — يظهر فقط عندما تكون ميزة السوق مفعّلة */}
                {networkEnabled && (
                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!newRequest.shareInNetwork}
                        onChange={(e) => setNewRequest({ ...newRequest, shareInNetwork: !e.target.checked })}
                      />
                      <span>{t('requests.shareRequestInNetworkHint')}</span>
                    </label>
                    <small className="checkbox-hint">لن يظهر الطلب في السوق</small>
                  </div>
                </div>
                )}

                {/* Floor for apartment rent only */}
                {newRequest.requestType === 'rent' && newRequest.propertyType === 'apartment' && (
                  <div className="form-row">
                    <div className="form-group" style={{ maxWidth: '50%' }}>
                      <label>{t('requests.floor')}</label>
                      {/* FIX 3: added empty placeholder option */}
                      <select
                        className="form-control"
                        value={newRequest.floor}
                        onChange={(e) => setNewRequest({ ...newRequest, floor: e.target.value })}
                      >
                        <option value="">{t('requests.floor')}</option>
                        <option value="any">{t('requests.floorAny')}</option>
                        <option value="ground">{t('requests.floorGround')}</option>
                        <option value="first">{t('requests.floorFirst')}</option>
                        <option value="annex">{t('requests.floorAnnex')}</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Direction */}
                {newRequest.propertyType === 'land' && (
                  <div className="form-row">
                    <div className="form-group" style={{ maxWidth: '50%' }}>
                      <label>{t('offers.direction')}</label>
                      <select
                        className="form-control"
                        value={newRequest.direction}
                        onChange={(e) => setNewRequest({ ...newRequest, direction: e.target.value })}
                      >
                        <option value="">{t('offers.selectDirection')}</option>
                        <option value="north">{t('offers.north')}</option>
                        <option value="south">{t('offers.south')}</option>
                        <option value="east">{t('offers.east')}</option>
                        <option value="west">{t('offers.west')}</option>
                        <option value="northeast">{t('offers.northeast')}</option>
                        <option value="northwest">{t('offers.northwest')}</option>
                        <option value="southeast">{t('offers.southeast')}</option>
                        <option value="southwest">{t('offers.southwest')}</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              )}
            </div>

            <div className="modal-footer step-footer">
              {(currentRequestStep === 'ai' || (typeof currentRequestStep === 'number' && currentRequestStep >= 1)) && (
                <div className="step-nav-buttons">
                  {/* السابق */}
                  {(currentRequestStep === 'ai' || currentRequestStep === 1 || (typeof currentRequestStep === 'number' && currentRequestStep >= 2)) && (
                    <button
                      className="btn btn-outline step-btn-prev"
                      onClick={() => currentRequestStep === 'ai' || currentRequestStep === 1 ? setCurrentRequestStep(0) : setCurrentRequestStep(s => s - 1)}
                      disabled={saving}
                    >
                      السابق
                    </button>
                  )}

                  {/* التالي — only on step 'ai' and step 1 */}
                  {(currentRequestStep === 'ai' || currentRequestStep === 1) && (
                    <button
                      className="btn btn-secondary step-btn-next"
                      onClick={async () => {
                        if (currentRequestStep === 'ai') {
                          const success = await handleAnalyzeRequest();
                          if (success) setCurrentRequestStep(1);
                          return;
                        }
                        if (currentRequestStep === 1) {
                          if (!newRequest.clientName || !newRequest.clientName.trim()) { toast.warning('يرجى إدخال اسم العميل'); return; }
                          if (!newRequest.clientPhone || !newRequest.clientPhone.trim()) { toast.warning('يرجى إدخال رقم الهاتف'); return; }
                        }
                        setCurrentRequestStep(s => s + 1);
                      }}
                      disabled={saving || (currentRequestStep === 'ai' && analyzingRequest)}
                    >
                      {currentRequestStep === 'ai' ? (
                        analyzingRequest ? (
                          <><Loader size={16} className="spinner" /> جاري التحليل...</>
                        ) : (
                          'تحليل النص'
                        )
                      ) : (
                        'التالي'
                      )}
                    </button>
                  )}

                  {/* حفظ — step 2 only */}
                  {currentRequestStep === 2 && (
                    <>
                      <button
                        className="btn btn-primary step-btn-save"
                        onClick={() => handleAddRequest()}
                        disabled={saving}
                      >
                        {saving ? (
                          <Loader size={16} className="spinner" />
                        ) : (
                          <Check size={16} />
                        )}
                        حفظ
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {showContactModal && selectedRequest && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <FaWhatsapp size={24} />
                {t('requests.contactClient')}
              </h2>
              <button className="modal-close" onClick={() => setShowContactModal(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="contact-client-info">
                <div className="client-avatar large">
                  {selectedRequest.clientName.charAt(0)}
                </div>
                <div>
                  <h3>{selectedRequest.clientName}</h3>
                  <p>{selectedRequest.clientPhone}</p>
                </div>
              </div>

              <div className="form-group">
                <label>اختر قالب الرسالة</label>
                <div className="message-templates">
                  {messageTemplates.map((template, idx) => (
                    <button 
                      key={idx}
                      className={`template-btn ${selectedMessage === template.value ? 'active' : ''}`}
                      onClick={() => setSelectedMessage(template.value)}
                    >
                      {template.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>{t('requests.whatsappMessage')}</label>
                <textarea 
                  className="form-control"
                  rows={4}
                  value={selectedMessage}
                  onChange={(e) => setSelectedMessage(e.target.value)}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowContactModal(false)}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-success" onClick={handleSendWhatsApp}>
                <Send size={18} />
                إرسال عبر واتساب
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Request Modal */}
      {showEditModal && editingRequest && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Edit size={24} />
                تعديل الطلب
              </h2>
              <button className="modal-close" onClick={handleCloseEditModal}>
                <X size={24} />
              </button>
            </div>

            {/* شريط الخطوات — قابل للنقر */}
            <div className="step-indicator" style={{ flexShrink: 0, background: 'var(--surface-color)' }}>
              {[
                { num: '١', label: 'بيانات العميل' },
                { num: '٢', label: 'تفاصيل الطلب' },
              ].map((step, index) => {
                const stepNum = index + 1;
                return (
                  <div
                    key={index}
                    className={`step-item ${editRequestStep === stepNum ? 'active' : ''} ${editRequestStep > stepNum ? 'done' : ''} clickable`}
                    onClick={() => setEditRequestStep(stepNum)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="step-dot">{step.num}</span>
                    <span className="step-label">{step.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="modal-body">

              {/* ===== STEP 1: بيانات العميل ===== */}
              {editRequestStep === 1 && (
              <div className="form-section">
                <h3>بيانات العميل</h3>
                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>{t('requests.clientName')} <span className="required">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingRequest.clientName}
                      onChange={(e) => setEditingRequest({ ...editingRequest, clientName: e.target.value })}
                      placeholder={t('requests.fullName')}
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('requests.clientPhone')} <span className="required">*</span></label>
                    <input
                      type="tel"
                      className="form-control"
                      value={editingRequest.clientPhone}
                      onChange={(e) => setEditingRequest({ ...editingRequest, clientPhone: e.target.value })}
                      placeholder="+966 55 559 0799"
                      dir="ltr"
                    />
                  </div>
                </div>

                {/* زر اظهار المزيد */}
                <button
                  type="button"
                  className="show-more-fields-btn"
                  onClick={() => setShowMoreEditRequestFields(v => !v)}
                >
                  {showMoreEditRequestFields ? '▲ إخفاء' : '▼ اظهار المزيد'}
                </button>

                {showMoreEditRequestFields && (
                <>
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('requests.clientSeriousness')}</label>
                    <select
                      className="form-control"
                      value={editingRequest.clientSeriousness || 'interested'}
                      onChange={(e) => setEditingRequest({ ...editingRequest, clientSeriousness: e.target.value })}
                    >
                      <option value="very_interested">{t('requests.veryInterested')}</option>
                      <option value="interested">{t('requests.interested')}</option>
                      <option value="potential">{t('requests.potential')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>{t('requests.lastContact')}</label>
                    <input
                      type="date"
                      className="form-control"
                      value={editingRequest.lastContact ? editingRequest.lastContact.split('T')[0] : ''}
                      onChange={(e) => setEditingRequest({ ...editingRequest, lastContact: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>الحالة</label>
                    <select
                      className="form-control"
                      value={editingRequest.status || 'not_updated'}
                      onChange={(e) => setEditingRequest({ ...editingRequest, status: e.target.value })}
                    >
                      <option value="not_updated">{t('requests.notUpdated')}</option>
                      <option value="updated">{t('requests.updated')}</option>
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>ملاحظات خاصة</label>
                    <textarea
                      className="form-control expandable-textarea"
                      rows={3}
                      value={editingRequest.notes || ''}
                      onChange={(e) => setEditingRequest({ ...editingRequest, notes: e.target.value })}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                      ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                      placeholder="ملاحظات خاصة بك عن هذا الطلب..."
                    />
                  </div>
                </div>
                </>
                )}
              </div>
              )}

              {/* ===== STEP 2: تفاصيل الطلب ===== */}
              {editRequestStep === 2 && (
              <div className="form-section">
                <h3>تفاصيل الطلب</h3>
                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>{t('requests.requestType')} <span className="required">*</span></label>
                    <select 
                      className="form-control"
                      value={editingRequest.requestType}
                      onChange={(e) => setEditingRequest({ ...editingRequest, requestType: e.target.value })}
                    >
                      <option value="buy">{t('requests.buy')}</option>
                      <option value="rent">{t('requests.rent')}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('offers.propertyType')} <span className="required">*</span></label>
                    <select 
                      className="form-control"
                      value={editingRequest.propertyType}
                      onChange={(e) => setEditingRequest({ ...editingRequest, propertyType: e.target.value })}
                    >
                      {propertyTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row three-cols">
                  <div className="form-group">
                    <label>{t('offers.city')} <span className="required">*</span></label>
                    <select 
                      className="form-control"
                      value={editingRequest.city}
                      onChange={(e) => setEditingRequest({ ...editingRequest, city: e.target.value, region: '', districts: [] })}
                      disabled={loadingLocations}
                    >
                      <option value="">{loadingLocations ? t('common.loading') : t('common.selectCity')}</option>
                      {cities.map(city => (
                        <option key={city._id} value={city.label}>{city.label}</option>
                      ))}
                      {editingRequest.city && !cities.some(c => c.label === editingRequest.city) && (
                        <option value={editingRequest.city}>{editingRequest.city}</option>
                      )}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>المنطقة</label>
                    <select
                      className="form-control"
                      value={editingRequest.region || ''}
                      onChange={(e) => setEditingRequest({ ...editingRequest, region: e.target.value, districts: [] })}
                      disabled={!editingRequest.city || loadingLocations}
                    >
                      <option value="">اختر المنطقة</option>
                      {editingRequest.city && getAreasForCity(editingRequest.city).map(area => (
                        <option key={area} value={area}>{area}</option>
                      ))}
                      {editingRequest.region && !getAreasForCity(editingRequest.city).includes(editingRequest.region) && (
                        <option value={editingRequest.region}>{editingRequest.region}</option>
                      )}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{t('requests.districts')}</label>
                    <MultiSelectSearchable
                      options={(editingRequest.region ? getDistrictsForArea(editingRequest.region) : getAllDistrictsForCity(editingRequest.city)).map(d => ({ value: d, label: d }))}
                      value={editingRequest.districts || []}
                      onChange={(selected) => setEditingRequest({ ...editingRequest, districts: selected })}
                      placeholder="اختر الأحياء المطلوبة"
                      searchPlaceholder="ابحث عن الحي..."
                      selectAllLabel="اختيار جميع الأحياء"
                      disabled={!editingRequest.city || loadingLocations}
                    />
                  </div>
                </div>

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>
                      {t('requests.budget')} (ر.س) <span className="required">*</span>
                      <label className="checkbox-label" style={{ display: 'inline-flex', marginRight: '12px', fontSize: '12px', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={editingRequest.noBudgetPreference || false}
                          onChange={(e) => setEditingRequest({ ...editingRequest, noBudgetPreference: e.target.checked, budget: e.target.checked ? '' : editingRequest.budget })}
                        />
                        <span>لا يهم</span>
                      </label>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingRequest.noBudgetPreference ? '' : formatNumberWithCommas(editingRequest.budget)}
                      onChange={(e) => setEditingRequest({ ...editingRequest, budget: parseFormattedNumber(e.target.value) })}
                      placeholder={editingRequest.noBudgetPreference ? 'لا يهم' : t('requests.budget')}
                      disabled={editingRequest.noBudgetPreference}
                    />
                  </div>
                  
                  {/* Conditional fields based on request type */}
                  {editingRequest.requestType === 'buy' ? (
                    <div className="form-group">
                      <label>{t('requests.paymentMethod')}</label>
                      <select 
                        className="form-control"
                        value={editingRequest.paymentMethod || ''}
                        onChange={(e) => setEditingRequest({ ...editingRequest, paymentMethod: e.target.value })}
                      >
                        <option value="">اختر</option>
                        <option value="cash">{t('requests.paymentCash')}</option>
                        <option value="bank">{t('requests.paymentBank')}</option>
                      </select>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>{t('requests.paymentPlan')}</label>
                      <select 
                        className="form-control"
                        value={editingRequest.paymentPlan || ''}
                        onChange={(e) => setEditingRequest({ ...editingRequest, paymentPlan: e.target.value })}
                      >
                        <option value="">اختر</option>
                        <option value="single">{t('requests.paymentSingle')}</option>
                        <option value="installments">{t('requests.paymentInstallments')}</option>
                        <option value="monthly">{t('requests.paymentMonthly')}</option>
                      </select>
                    </div>
                  )}
                </div>

                <div className="form-row two-cols">
                  <div className="form-group">
                    <label>
                      {t('offers.area')} (م²)
                      <label className="checkbox-label" style={{ display: 'inline-flex', marginRight: '12px', fontSize: '12px', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={editingRequest.noAreaPreference || false}
                          onChange={(e) => setEditingRequest({ ...editingRequest, noAreaPreference: e.target.checked, area: e.target.checked ? '' : editingRequest.area, areaMin: e.target.checked ? 0 : editingRequest.areaMin })}
                        />
                        <span>لا يهم</span>
                      </label>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={editingRequest.area || editingRequest.areaMin || ''}
                      onChange={(e) => setEditingRequest({ ...editingRequest, area: e.target.value, areaMin: Number(e.target.value) })}
                      placeholder={editingRequest.noAreaPreference ? 'لا يهم' : t('offers.area')}
                      disabled={editingRequest.noAreaPreference}
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      {t('requests.age')} (سنة)
                      <label className="checkbox-label" style={{ display: 'inline-flex', marginRight: '12px', fontSize: '12px', fontWeight: 'normal' }}>
                        <input
                          type="checkbox"
                          checked={editingRequest.noAgePreference || false}
                          onChange={(e) => setEditingRequest({ ...editingRequest, noAgePreference: e.target.checked, age: e.target.checked ? '' : editingRequest.age })}
                        />
                        <span>لا يهم</span>
                      </label>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={editingRequest.age || ''}
                      onChange={(e) => setEditingRequest({ ...editingRequest, age: e.target.value })}
                      placeholder={editingRequest.noAgePreference ? 'لا يهم' : t('requests.age')}
                      disabled={editingRequest.noAgePreference}
                    />
                  </div>
                </div>

                {/* Land-specific fields */}
                {editingRequest.propertyType === 'land' && (
                  <div className="form-row three-cols">
                    <div className="form-group">
                      <label>{t('offers.landPurpose')}</label>
                      <select 
                        className="form-control"
                        value={editingRequest.landPurpose || ''}
                        onChange={(e) => setEditingRequest({ ...editingRequest, landPurpose: e.target.value })}
                      >
                        <option value="">{t('common.select') || 'اختر'}</option>
                        <option value="residential">{t('offers.purposeResidential')}</option>
                        <option value="agricultural">{t('offers.purposeAgricultural')}</option>
                        <option value="industrial">{t('offers.purposeIndustrial')}</option>
                        <option value="commercial">{t('offers.purposeCommercial')}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>{t('offers.street')} (م)</label>
                      <input 
                        type="number"
                        className="form-control"
                        value={editingRequest.streetWidth || ''}
                        onChange={(e) => setEditingRequest({ ...editingRequest, streetWidth: e.target.value })}
                        placeholder={t('offers.street')}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t('offers.direction')}</label>
                      <select 
                        className="form-control"
                        value={editingRequest.direction || ''}
                        onChange={(e) => setEditingRequest({ ...editingRequest, direction: e.target.value })}
                      >
                        <option value="">{t('offers.selectDirection')}</option>
                        <option value="north">{t('offers.north')}</option>
                        <option value="south">{t('offers.south')}</option>
                        <option value="east">{t('offers.east')}</option>
                        <option value="west">{t('offers.west')}</option>
                        <option value="northeast">{t('offers.northeast')}</option>
                        <option value="northwest">{t('offers.northwest')}</option>
                        <option value="southeast">{t('offers.southeast')}</option>
                        <option value="southwest">{t('offers.southwest')}</option>
                      </select>
                    </div>
                  </div>
                )}

                {editingRequest.requestType === 'rent' && (
                  <div className="form-row">
                    <div className="form-group" style={{ maxWidth: '50%' }}>
                      <label>{t('requests.floor')}</label>
                      <select
                        className="form-control"
                        value={editingRequest.floor || 'any'}
                        onChange={(e) => setEditingRequest({ ...editingRequest, floor: e.target.value })}
                      >
                        <option value="any">{t('requests.floorAny')}</option>
                        <option value="ground">{t('requests.floorGround')}</option>
                        <option value="first">{t('requests.floorFirst')}</option>
                        <option value="annex">{t('requests.floorAnnex')}</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('requests.requirements')}</label>
                    <textarea
                      className="form-control expandable-textarea"
                      rows={4}
                      value={editingRequest.requirements || ''}
                      onChange={(e) => setEditingRequest({ ...editingRequest, requirements: e.target.value })}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                      ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                      placeholder={t('requests.requirements')}
                    />
                  </div>
                </div>
              </div>
              )}

            </div>

            {/* أزرار التنقل */}
            <div className="modal-footer step-footer">
              <div className="step-nav-buttons">
                {editRequestStep > 1 ? (
                  <button className="btn btn-outline step-btn-prev" onClick={() => setEditRequestStep(s => s - 1)} disabled={saving}>السابق</button>
                ) : (
                  <button className="btn btn-outline step-btn-prev" onClick={handleCloseEditModal}>{t('common.cancel')}</button>
                )}
                {editRequestStep < 2 && (
                  <button
                    className="btn btn-secondary step-btn-next"
                    onClick={() => {
                      if (!editingRequest.clientName?.trim()) { toast.warning('يرجى إدخال اسم العميل'); return; }
                      if (!editingRequest.clientPhone?.trim()) { toast.warning('يرجى إدخال رقم الهاتف'); return; }
                      setEditRequestStep(2);
                    }}
                    disabled={saving}
                  >التالي</button>
                )}
                {editRequestStep >= 1 && (
                  <button className="btn btn-primary step-btn-save" onClick={handleSaveEdit} disabled={saving}>
                    {saving ? <><Loader size={16} className="spinner" /> جاري الحفظ...</> : 'حفظ'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Request Modal */}
      {showViewRequestModal && viewingRequest && (
        <div className="modal-overlay" onClick={() => { cancelNewRequestMatchFlow(); setShowViewRequestModal(false); setViewingRequest(null); }}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            {/* Floating close button — replaces sticky header */}
            <button className="modal-close modal-close-floating" onClick={() => { cancelNewRequestMatchFlow(); setShowViewRequestModal(false); setViewingRequest(null); }}>
              <X size={24} />
            </button>
            <div className="modal-body">
              <div className="view-request-content">
                {(() => {
                  const matchedOffersCount = Number(viewingRequest.matchingOffersCount ?? viewingRequest.matchesCount ?? 0);
                  const missingFieldsCount = getMissingRequestFields(viewingRequest).length;
                  const requestAddedAt = viewingRequest.addedAt || viewingRequest.createdAt;
                  const isPendingPublicRequest = viewingRequest.source === 'public_marketer_site' && viewingRequest.approvalStatus === 'pending';
                  const budgetValue = Number(viewingRequest.budget) > 0
                    ? formatPrice(viewingRequest.budget)
                    : (viewingRequest.noBudgetPreference ? 'لا يهم' : 'غير محددة');
                  const areaValue = Number(viewingRequest.area || viewingRequest.areaMin) > 0
                    ? formatArea(viewingRequest.area || viewingRequest.areaMin)
                    : (viewingRequest.noAreaPreference ? 'لا يهم' : 'غير محددة');

                  return (
                    <div className="request-preview-panel">
                      <div className="view-request-header">
                        <div className="client-avatar large">{viewingRequest.clientName?.charAt(0) || 'ع'}</div>
                        <div className="client-info-view">
                          <h3>{viewingRequest.clientName}</h3>
                          <div className="client-phone-view">
                            <Phone size={16} />
                            <span dir="ltr">{viewingRequest.clientPhone}</span>
                            <a href={`https://wa.me/966${viewingRequest.clientPhone?.replace(/^0/, '')}`} target="_blank" rel="noopener noreferrer" className="whatsapp-link" aria-label="واتساب">
                              <FaWhatsapp size={16} />
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="request-preview-badges">
                        {isPendingPublicRequest && (
                          <span className="request-preview-badge public-request-modal-badge">وارد من موقعي العقاري — بانتظار الموافقة</span>
                        )}
                        <span className="request-preview-badge success">{getStatusLabel(viewingRequest.status)}</span>
                        <span className="request-preview-badge warning">{missingFieldsCount} حقول ناقصة</span>
                        <span className="request-preview-badge info">{matchedOffersCount} عرض مطابق</span>
                        {requestAddedAt && (
                          <span className="request-preview-badge info">أضيف: {formatGregorianDate(requestAddedAt)}</span>
                        )}
                      </div>

                      {/* ── Unified specs grid — all fields in one card grid ── */}
                      <div className="view-specs-grid">
                        <div className="spec-card">
                          <Home size={20} />
                          <div className="spec-info">
                            <span className="spec-label">نوع الطلب</span>
                            <span className="spec-value">{viewingRequest.requestType === 'buy' ? t('requests.buy') : t('requests.rent')}</span>
                          </div>
                        </div>
                        <div className="spec-card">
                          <Building2 size={20} />
                          <div className="spec-info">
                            <span className="spec-label">نوع العقار</span>
                            <span className="spec-value">{propertyTypes.find(pt => pt.value === viewingRequest.propertyType)?.label || 'غير محدد'}</span>
                          </div>
                        </div>
                        <div className="spec-card">
                          <MapPin size={20} />
                          <div className="spec-info">
                            <span className="spec-label">المدينة</span>
                            <span className="spec-value">{viewingRequest.city || 'غير محدد'}</span>
                          </div>
                        </div>
                        {(viewingRequest.districts?.length > 0 || viewingRequest.district) && (
                          <div className="spec-card">
                            <MapPin size={20} />
                            <div className="spec-info">
                              <span className="spec-label">الحي</span>
                              <span className="spec-value">{viewingRequest.districts?.length > 0 ? viewingRequest.districts.join('، ') : viewingRequest.district}</span>
                            </div>
                          </div>
                        )}
                        <div className="spec-card">
                          <ArrowUpDown size={20} />
                          <div className="spec-info">
                            <span className="spec-label">الميزانية</span>
                            <span className="spec-value">{budgetValue}</span>
                          </div>
                        </div>
                        <div className="spec-card">
                          <ArrowUpDown size={20} />
                          <div className="spec-info">
                            <span className="spec-label">المساحة</span>
                            <span className="spec-value">{areaValue}</span>
                          </div>
                        </div>
                        {Number(viewingRequest.age) > 0 && (
                          <div className="spec-card">
                            <Home size={20} />
                            <div className="spec-info">
                              <span className="spec-label">العمر</span>
                              <span className="spec-value">{viewingRequest.age} سنة</span>
                            </div>
                          </div>
                        )}
                        {viewingRequest.clientSeriousness && (
                          <div className="spec-card">
                            <Eye size={20} />
                            <div className="spec-info">
                              <span className="spec-label">جدية العميل</span>
                              <span className="spec-value">
                                {viewingRequest.clientSeriousness === 'very_interested' && t('requests.veryInterested')}
                                {viewingRequest.clientSeriousness === 'interested' && t('requests.interested')}
                                {viewingRequest.clientSeriousness === 'potential' && t('requests.potential')}
                              </span>
                            </div>
                          </div>
                        )}
                        {viewingRequest.lastContact && (
                          <div className="spec-card">
                            <Bell size={20} />
                            <div className="spec-info">
                              <span className="spec-label">آخر تواصل</span>
                              <span className="spec-value">{formatGregorianDate(viewingRequest.lastContact)}</span>
                            </div>
                          </div>
                        )}
                        {viewingRequest.clientPhone && (
                          <div className="spec-card">
                            <Phone size={20} />
                            <div className="spec-info">
                              <span className="spec-label">رقم الهاتف</span>
                              <span className="spec-value" dir="ltr">{viewingRequest.clientPhone}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* ── المتطلبات + الملاحظات ── */}
                      <section className="request-preview-section">
                        <h4 className="request-preview-section-title">المتطلبات والملاحظات</h4>
                        <div className="request-preview-details-text">
                          {viewingRequest.requirements ? (
                            <p dangerouslySetInnerHTML={{
                              __html: (viewingRequest.requirements || '').replace(
                                /(https?:\/\/[^\s]+)/g,
                                '<a href="$1" target="_blank" rel="noopener noreferrer" class="inline-link">$1</a>'
                              )
                            }} />
                          ) : (
                            <p>لا توجد تفاصيل إضافية.</p>
                          )}
                          {viewingRequest.notes && (
                            <p className="request-preview-notes">{viewingRequest.notes}</p>
                          )}
                        </div>
                      </section>

                      <section className="request-preview-section view-request-matches" onClick={(e) => e.stopPropagation()}>
                        <div className="view-request-matches-head">
                          <h4 className="request-preview-section-title">العروض المطابقة</h4>
                          <button
                            type="button"
                            className="mrc-matches-toggle"
                            onClick={handleToggleViewRequestMatches}
                          >
                            {showViewRequestMatches
                              ? 'إخفاء المطابقات'
                              : `عرض المطابقات (${matchedOffersCount})`}
                          </button>
                        </div>

                        {showViewRequestMatches && (
                          renderRequestMatchesList({
                            requestMatches: viewRequestMatches,
                            loading: viewRequestMatchesLoading,
                            error: viewRequestMatchesError,
                            onViewOffer: handleViewOffer
                          })
                        )}
                      </section>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="modal-footer view-modal-footer">
              {viewingRequest.source === 'public_marketer_site' && viewingRequest.approvalStatus === 'pending' && (
                <>
                  <button className="btn btn-success" onClick={() => handleReviewPublicRequest(viewingRequest, 'approve')} disabled={saving}>
                    <Check size={16} />
                    قبول
                  </button>
                  <button className="btn btn-danger" onClick={() => handleReviewPublicRequest(viewingRequest, 'reject')} disabled={saving}>
                    <X size={16} />
                    رفض
                  </button>
                  <button className="btn btn-secondary" onClick={() => handlePublicRequestWhatsApp(viewingRequest)}>
                    <FaWhatsapp size={16} />
                    تواصل واتساب
                  </button>
                </>
              )}
              <button className="btn btn-primary" onClick={() => { cancelNewRequestMatchFlow(); setShowViewRequestModal(false); handleOpenEdit(viewingRequest); }}>
                <Edit size={16} />
                تعديل
              </button>
              <button className="btn btn-secondary" onClick={() => handleShareRequest(viewingRequest)}>
                <Share2 size={16} />
                مشاركة
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && requestToDelete && (
        <div className="modal-overlay" onClick={() => { setShowDeleteModal(false); setRequestToDelete(null); }}>
          <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Archive size={24} />
                أرشفة الطلب
              </h2>
              <button className="modal-close" onClick={() => { setShowDeleteModal(false); setRequestToDelete(null); }}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p className="delete-message">هل تريد أرشفة هذا الطلب؟ يمكنك استعادته لاحقاً.</p>
              <div className="delete-preview">
                <div className="client-avatar">{requestToDelete.clientName.charAt(0)}</div>
                <div>
                  <h4>{requestToDelete.clientName}</h4>
                  <p>{requestToDelete.city} - {propertyTypes.find(t => t.value === requestToDelete.requestType)?.label}</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setShowDeleteModal(false); setRequestToDelete(null); }}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-danger" onClick={handleConfirmDelete} disabled={saving}>
                {saving ? <Loader size={18} className="spinner" /> : <Archive size={18} />}
                أرشفة
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Phone Warning Modal */}
      {showDuplicateWarning && duplicateRequests.length > 0 && (
        <div className="modal-overlay" onClick={() => { setShowDuplicateWarning(false); setDuplicateRequests([]); setPendingRequestData(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Phone size={24} />
                تنبيه: رقم الهاتف موجود مسبقاً
              </h2>
              <button className="modal-close" onClick={() => { setShowDuplicateWarning(false); setDuplicateRequests([]); setPendingRequestData(null); }}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '16px', color: '#e67e22', fontWeight: 'bold' }}>
                ⚠️ يوجد {duplicateRequests.length} طلب مسبق بنفس رقم الهاتف. هل تريد الاستمرار؟
              </p>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {duplicateRequests.map((req, idx) => (
                  <div key={req._id || idx} style={{
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <strong>{req.clientName}</strong>
                      <span style={{ fontSize: '12px', color: '#6c757d' }}>
                        {formatGregorianDate(req.createdAt)}
                      </span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#495057' }}>
                      <span>{req.clientPhone}</span>
                      <span style={{ margin: '0 8px' }}>|</span>
                      <span>{propertyTypes.find(pt => pt.value === req.propertyType)?.label || req.propertyType}</span>
                      <span style={{ margin: '0 8px' }}>|</span>
                      <span>{req.city}</span>
                      {req.districts && req.districts.length > 0 && (
                        <span> - {req.districts.join(', ')}</span>
                      )}
                      {req.budget > 0 && (
                        <span style={{ margin: '0 8px' }}>| {formatPrice(req.budget)}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setShowDuplicateWarning(false); setDuplicateRequests([]); setPendingRequestData(null); }}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-primary" onClick={handleConfirmDuplicate} disabled={saving}>
                {saving ? <Loader size={18} className="spinner" /> : <Plus size={18} />}
                إضافة على أي حال
              </button>
            </div>
          </div>
        </div>
      )}

      <OfferDetailsModal
        isOpen={showViewOfferModal}
        offer={selectedMatch?.offer || null}
        propertyTypes={propertyTypes}
        onClose={() => {
          setShowViewOfferModal(false);
          setSelectedMatch(null);
        }}
      />

      {/* Network Visibility Modal */}
      {showNetworkModal && networkRequest && (
        <div className="modal-overlay" onClick={() => { setShowNetworkModal(false); setNetworkRequest(null); }}>
          <div className="modal-content network-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Network size={24} />
                {networkRequest.shareInNetwork ? 'إزالة من الشبكة' : 'إضافة إلى الشبكة'}
              </h2>
              <button className="modal-close" onClick={() => { setShowNetworkModal(false); setNetworkRequest(null); }}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="network-request-preview">
                <div className="preview-icon">
                  {networkRequest.propertyType === 'villa' ? <Home size={32} /> : 
                   networkRequest.propertyType === 'land' ? <Landmark size={32} /> : 
                   <Building2 size={32} />}
                </div>
                <div className="preview-info">
                  <h4>{networkRequest.clientName || 'عميل'}</h4>
                  <p>{networkRequest.city}{networkRequest.district ? ` - ${networkRequest.district}` : ''}</p>
                  <span className="budget">{formatPrice(networkRequest.budget)}</span>
                </div>
              </div>

              <div className="network-info-box">
                {networkRequest.shareInNetwork ? (
                  <>
                    <p><strong>هل تريد إزالة هذا الطلب من الشبكة؟</strong></p>
                    <p className="hint">لن يظهر الطلب للمسوقين الآخرين بعد الإزالة.</p>
                  </>
                ) : (
                  <>
                    <p><strong>هل تريد إضافة هذا الطلب إلى الشبكة؟</strong></p>
                    <p className="hint">ملاحظة: سيتم مشاركة جميع بيانات الطلب ما عدا:</p>
                    <ul className="private-fields-list">
                      <li>اسم العميل</li>
                      <li>رقم الجوال</li>
                      <li>جدية العميل</li>
                      <li>آخر تواصل</li>
                    </ul>
                    <p className="hint">الحقول الخاصة تظهر لك فقط.</p>
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer network-footer">
              <button className="btn btn-outline" onClick={() => { setShowNetworkModal(false); setNetworkRequest(null); }}>
                {t('common.cancel')}
              </button>
              <button 
                className={`btn ${networkRequest.shareInNetwork ? 'btn-danger' : 'btn-primary'}`} 
                onClick={handleSaveNetworkSettings}
              >
                {networkRequest.shareInNetwork ? (
                  <>
                    <X size={18} />
                    إزالة من الشبكة
                  </>
                ) : (
                  <>
                    <Check size={18} />
                    إضافة إلى الشبكة
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Matching Results Popup after adding request */}
      {showMatchingPopup && (
        <div className="modal-overlay" onClick={() => { setShowMatchingPopup(false); setMatchingPopupResults([]); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <Sparkles size={24} />
                عروض مطابقة لهذا الطلب
              </h2>
              <button className="modal-close" onClick={() => { setShowMatchingPopup(false); setMatchingPopupResults([]); }}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              {renderRequestMatchesList({
                requestMatches: matchingPopupResults,
                loading: matchingPopupLoading,
                error: '',
                // Close only the match popup before opening the existing offer details.
                onViewOffer: (match) => {
                  setShowMatchingPopup(false);
                  handleViewOffer(match);
                }
              })}
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => { setShowMatchingPopup(false); setMatchingPopupResults([]); }}>
                إغلاق
              </button>
              {matchingPopupResults.length > 0 && (
                <button className="btn btn-primary" onClick={() => {
                  setShowMatchingPopup(false);
                  setMatchingPopupResults([]);
                  setActiveTab('matching');
                  fetchMatches();
                }}>
                  <Sparkles size={18} />
                  عرض كل المطابقات
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
    </PermissionGate>
  );
};

export default RequestsAndMatchingPage;
